/**
 * FlashBookStudio — interactive book creation flow.
 *
 * Step 1 – Name, description & cover (image + audio, short video ≤ 60s, or image)
 * Step 2 – Theme picker (FlashBook default themes + FlashBook HTML themes)
 * Step 3 – Page editor:
 *    • bottom page chips + "Add page" button
 *    • each page is a scrollable stack of blocks; in-page "+" opens a grouped
 *      block picker with the same 13 types as capsules
 *    • per-block editors reuse the capsule studio editors (shared widgets)
 *    • preview: current page only, or the whole book
 *
 * The studio's *own* furniture — header, page bar, page sheet, block cards,
 * fields, sheets — wears the app's appearance (light / dim / dark) through
 * `useStudioChrome`, so a creator who reads the app in light mode creates in
 * light mode. The chosen FlashBook theme is not lost by that: a block's in-place
 * preview and the preview modal both render the real reader, so the theme's own
 * paper, type and ornaments appear where the book is actually being shown.
 */
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  type StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import * as ImagePicker from 'expo-image-picker'
import {LinearGradient} from 'expo-linear-gradient'
import {AtUri} from '@atproto/api'
import {Feather, Ionicons} from '@expo/vector-icons'
import {type MessageDescriptor} from '@lingui/core'
import {useLingui} from '@lingui/react/macro'
import {useIsFocused, useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'
import {nanoid} from 'nanoid/non-secure'

import {
  type BackgroundMusicChoice,
  musicChoiceFromRecord,
  musicChoiceToRecord,
} from '#/lib/backgroundMusic'
import {validateSlide} from '#/lib/capsuleValidation'
import {COVER_VIDEO_MAX_SECONDS, coverNeedsTrim} from '#/lib/coverTrim'
import {categoryOfLevel} from '#/lib/curriculum'
import {stopReadingAudio} from '#/lib/flashbookAudio'
import {canEditAudio} from '#/lib/flashbookAudioEditor'
import {rememberLocalClip} from '#/lib/flashbookClips'
import {flashBookContentSignature} from '#/lib/flashbookContentSignature'
import {coverPickKind} from '#/lib/flashbookCoverPick'
import {
  flashBookDraftCoverOf,
  type FlashBookDraftRef,
  forgetFlashBookDraft,
  rememberFlashBookDraft,
} from '#/lib/flashbookDrafts'
import {
  buildFlashBookFixture,
  FIXTURE_COVER_FILM_URI,
  FIXTURE_UNREADABLE_AUDIO,
  withLevelledReadings,
  withReadingAudio,
} from '#/lib/flashbookFixtures'
import {
  COVER_CLIP_ID,
  type LevelledReadingClip,
  readingClipsOf,
} from '#/lib/flashbookLevelMatch'
import {
  bookHasContent,
  type FlashBookRecoveredBook,
  type FlashBookSnapshot,
  getFlashBookSnapshot,
  offerableSnapshot,
} from '#/lib/flashbookRecovery'
import {
  kindSpeaks,
  RICH_KIND_META,
  RICH_PICKER_ENTRIES,
} from '#/lib/richBlockKinds'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {createSlide} from '#/lib/slideFactory'
import {CONTENT_KIND_ACCENT} from '#/lib/slideTokens'
import {buildStudioPickerSections} from '#/lib/studioBlockPicker'
import {
  useAddClassroomContentMutation,
  useClassroomQuery,
} from '#/state/queries/classrooms'
import {createActorFlashbookDraftsQueryKey} from '#/state/queries/flashbook'
import {useAgent} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {AccordionSlideEditor} from '#/screens/CapsuleStudio/components/slides/AccordionSlideEditor'
import {ComplexFillBlankSlideEditor} from '#/screens/CapsuleStudio/components/slides/ComplexFillBlankSlideEditor'
import {ContentSlideEditor} from '#/screens/CapsuleStudio/components/slides/ContentSlideEditor'
import {DragWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/DragWordsSlideEditor'
import {FillBlankSlideEditor} from '#/screens/CapsuleStudio/components/slides/FillBlankSlideEditor'
import {FillBlanksSlideEditor} from '#/screens/CapsuleStudio/components/slides/FillBlanksSlideEditor'
import {FindWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/FindWordsSlideEditor'
import {ImageChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/ImageChoiceSlideEditor'
import {MarkWordsSlideEditor} from '#/screens/CapsuleStudio/components/slides/MarkWordsSlideEditor'
import {MatchingSlideEditor} from '#/screens/CapsuleStudio/components/slides/MatchingSlideEditor'
import {MultiChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/MultiChoiceSlideEditor'
import {OrderingSlideEditor} from '#/screens/CapsuleStudio/components/slides/OrderingSlideEditor'
import {SingleChoiceSlideEditor} from '#/screens/CapsuleStudio/components/slides/SingleChoiceSlideEditor'
import {TrueFalseSlideEditor} from '#/screens/CapsuleStudio/components/slides/TrueFalseSlideEditor'
import {showClassroomError} from '#/screens/Classroom/components/showClassroomError'
import {FlashBookThemeSelectorView} from '#/screens/FlashBookThemeSelector'
import {atoms as a, useBreakpoints} from '#/alf'
import {platform} from '#/alf/util/platform'
import {useDialogControl} from '#/components/Dialog'
import {FlashBookAudioField} from '#/components/flashbooks/FlashBookAudioField'
import {FlashBookAutosaveNote} from '#/components/flashbooks/FlashBookAutosaveNote'
import {FlashBookBlockPlayer} from '#/components/flashbooks/FlashBookBlockPlayer'
import {FlashBookDraftsDialog} from '#/components/flashbooks/FlashBookDraftsDialog'
import {FlashBookLeaveDialog} from '#/components/flashbooks/FlashBookLeaveDialog'
import {FlashBookLevelMatchDialog} from '#/components/flashbooks/FlashBookLevelMatchDialog'
import {FlashBookPreviewModal} from '#/components/flashbooks/FlashBookPreviewModal'
import {FlashBookRecoveryDialog} from '#/components/flashbooks/FlashBookRecoveryDialog'
import {
  saveFlashBookDraft,
  uploadClip,
} from '#/components/flashbooks/flashbookSave'
import {PublishFlashBookDialog} from '#/components/flashbooks/PublishFlashBookDialog'
import {useFlashBookAutosave} from '#/components/flashbooks/useFlashBookAutosave'
import {useFlashBookDrafts} from '#/components/flashbooks/useFlashBookDrafts'
import {useFlashBookLevelMatch} from '#/components/flashbooks/useFlashBookLevelMatch'
import * as Layout from '#/components/Layout'
import {BackgroundMusicSheet} from '#/components/studio/BackgroundMusicSheet'
import {RichBlockEditor} from '#/components/studio/RichBlockEditor'
import {StudioBlockPicker} from '#/components/studio/StudioBlockPicker'
import {StudioConfirmDialog} from '#/components/studio/StudioConfirmDialog'
import {StudioCoverTrimSheet} from '#/components/studio/StudioCoverTrimSheet'
import {FirstFieldFocus} from '#/components/studio/StudioFirstFieldFocus'
import {
  type StudioIssue,
  StudioIssuesDialog,
} from '#/components/studio/StudioIssuesDialog'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {StudioWindowVideo} from '#/components/studio/StudioWindowVideo'
import {IS_DEV} from '#/env'
import {getFlashbookDesign} from '#/themes-flashbook/design'
import {getFlashbookTheme} from '#/themes-flashbook/registry'
import {
  cardBorder,
  readableAccent,
  type StudioChrome,
} from '#/themes-studio/chrome'
import {OVER_ART} from '#/themes-studio/overArt'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {
  type Slide,
  SLIDE_TYPE_META,
  slideBlockKey,
  type SlideType,
} from '#/types/capsule'
import {
  type FlashBookCover,
  type FlashBookModel,
  type FlashBookPage,
} from '#/types/flashbook'

/** Every block the studio can add, grouped the same way the capsule studio's are. */
const BLOCK_PICKER_SECTIONS = buildStudioPickerSections(RICH_PICKER_ENTRIES)

/**
 * What a block calls itself in the studio header: a rich block says what it is
 * (Quote, Table…), never a generic "Content".
 */
function blockLabelOf(
  block: Slide,
  l: (d: MessageDescriptor) => string,
): string {
  if (block.type === 'content') {
    return l(RICH_KIND_META[block.contentKind ?? 'text'].label)
  }
  return SLIDE_TYPE_META[block.type]?.label ?? block.type
}

// ── Per-block editor (reuses capsule studio editors) ─────────────────────────

function FlashBookBlockEditor({
  block,
  onChange,
  accent,
}: {
  block: Slide
  onChange: (data: Partial<Slide>) => void
  accent: string
}) {
  const fb = useStyles()
  const {t: l} = useLingui()
  // No `onPreview`: the studio's block header owns the preview action, and the
  // shared editors hide their own header (badge + Preview) when they get none,
  // so the type label is never shown twice.
  const props = {slide: block, onChange, primaryColor: accent}

  if (block.type === 'content') {
    return <FlashBookContentEditor {...props} />
  }

  switch (block.type) {
    case 'true_false':
      return <TrueFalseSlideEditor {...props} />
    case 'single_choice':
      return <SingleChoiceSlideEditor {...props} />
    case 'multiple_choice':
      return <MultiChoiceSlideEditor {...props} />
    case 'image_choice':
      return <ImageChoiceSlideEditor {...props} />
    case 'matching':
      return <MatchingSlideEditor {...props} />
    case 'ordering':
      return <OrderingSlideEditor {...props} />
    case 'fill_blank':
      return <FillBlankSlideEditor {...props} />
    case 'fill_blanks':
      return <FillBlanksSlideEditor {...props} />
    case 'accordion':
      return <AccordionSlideEditor {...props} />
    case 'drag_words':
      return <DragWordsSlideEditor {...props} />
    case 'mark_words':
      return <MarkWordsSlideEditor {...props} />
    case 'find_words':
      return <FindWordsSlideEditor {...props} />
    case 'complex_fill_blank':
      return <ComplexFillBlankSlideEditor {...props} />
    default:
      return (
        <View style={fb.stub}>
          <Text style={fb.stubTxt}>
            {l`Unsupported block type: ${block.type}`}
          </Text>
        </View>
      )
  }
}

/**
 * Content block editor with reading audio.
 *
 * The audio half is `FlashBookAudioField`: record with the microphone, upload a
 * file, or preview the device voice — each of which ends up in the same player,
 * so the creator hears exactly what the learner will hear.
 */
function FlashBookContentEditor({
  slide,
  onChange,
  primaryColor,
}: {
  slide: Slide
  onChange: (data: Partial<Slide>) => void
  primaryColor: string
}) {
  const fb = useStyles()
  const kind = slide.contentKind ?? 'text'

  // A table and a formula are notation — there is nothing to narrate, and an
  // audio control under a data grid is noise. Asked of the kinds rather than
  // restated here: the capsule's editor, both play surfaces and this one all put
  // the same question, and a rule copied into four places is four places to
  // disagree (see `#/lib/richBlockKinds`).
  const speaks = kindSpeaks(kind)

  return (
    <View>
      {/* The block grows with its text — the page stack around it already
          scrolls — so the text fields are never clipped and the audio actions
          always sit at the bottom edge of the block card. */}
      <View style={fb.contentBody}>
        {kind === 'text' ? (
          <ContentSlideEditor
            slide={slide}
            onChange={onChange}
            primaryColor={primaryColor}
            inline
            // The band below owns the voice: this card paints it at the card's
            // bottom edge, outside the region that scrolls, so a second field
            // inside the editor would be one block with two record buttons.
            ownsAudio={false}
          />
        ) : (
          <RichBlockEditor
            slide={slide}
            onChange={onChange}
            accent={primaryColor}
          />
        )}
      </View>
      {speaks && (
        <View style={fb.audioBar}>
          <FlashBookAudioField
            url={slide.readingAudioUrl}
            accent={primaryColor}
            onChange={readingAudioUrl => onChange({readingAudioUrl})}
          />
        </View>
      )}
    </View>
  )
}

// ── Cover step ────────────────────────────────────────────────────────────────

/**
 * Media used by the cover. A URL loaded back from a saved draft is just as valid
 * here as a file freshly picked from the device, which is why only the URI and
 * the *length* are kept: the length is what says whether a film needs a window.
 */
type PickedMedia = {uri: string; durationMs?: number}

/**
 * A picked film as a cover — or `null` when its minute is the creator's to choose.
 *
 * The rule in one place, because two doors lead to it: the picker, and the seeded
 * film a browser test drives the screen with. A film that fits *is* the cover and
 * brings its own sound, so a picture and a narration both have nothing to do; one
 * that runs over becomes a candidate for the trimmer instead.
 */
function coverFromPickedFilm(film: PickedMedia): CoverDraft | null {
  if (coverNeedsTrim(film.durationMs)) return null
  return {image: null, audio: null, video: film, videoStartMs: undefined}
}

type CoverDraft = {
  image: PickedMedia | null
  audio: PickedMedia | null
  video: PickedMedia | null
  /** Which minute of a long film the cover opens on (see `flashbookCoverTrim`). */
  videoStartMs?: number
}

/**
 * What a cover *is*, read off what it has.
 *
 * The studio used to carry a `kind` beside the picks, chosen by hand from a row
 * of pills — and a mode that can disagree with the fields is a mode that will.
 * A draft saved as `audio` with its narration since removed would offer the
 * learner a play button with nothing behind it, and a cover whose picture was
 * swapped for a film by a flow that forgot to set the mode would play as a still.
 * Deriving it means there is nothing to keep in sync: the fields are the
 * authority, and the record's `kind` is a reading of them for whoever consumes
 * the record (the player branches on it, and the reader should not have to probe
 * three fields to know what it is looking at).
 */
function coverKindOf(cover: CoverDraft): FlashBookCover['kind'] {
  if (cover.video) return 'video'
  if (cover.audio) return 'audio'
  return 'image'
}

/**
 * The cover as a record carries it.
 *
 * Two shapes meet here — the studio's three nullable picks and the flat record —
 * and a save, a recovery snapshot and a publish all have to cross between them.
 * Written once so the crossing is one thing to get right rather than three that
 * look alike.
 */
function coverRecordOf(cover: CoverDraft): FlashBookCover {
  return {
    kind: coverKindOf(cover),
    imageUri: cover.image?.uri,
    audioUri: cover.audio?.uri,
    videoUri: cover.video?.uri,
    videoStartMs: cover.video ? cover.videoStartMs : undefined,
  }
}

/**
 * The cover for the content fingerprint: the picks, plus what they add up to.
 *
 * `coverRecordOf` looks like the same thing and is not: the fingerprint reads the
 * picks by their studio names (`image.uri`), while a record spells them `imageUri`.
 * Passing the record here would fingerprint four empty strings, quietly turning
 * the unsaved-work guard into one that never notices a changed cover.
 */
function coverSignatureOf(cover: CoverDraft) {
  return {...cover, kind: coverKindOf(cover)}
}

/** A saved cover back into the studio's own shape. */
function coverDraftFrom(saved?: FlashBookCover): CoverDraft {
  return saved
    ? {
        image: saved.imageUri ? {uri: saved.imageUri} : null,
        audio: saved.audioUri ? {uri: saved.audioUri} : null,
        video: saved.videoUri ? {uri: saved.videoUri} : null,
        // A saved film's length is not in the record, so a re-opened draft has no
        // trimmer to offer — but the window it was saved with still plays.
        videoStartMs: saved.videoStartMs,
      }
    : {image: null, audio: null, video: null}
}

function CoverStep({
  title,
  setTitle,
  description,
  setDescription,
  cover,
  setCover,
  onNext,
  active,
  seededFilmMs,
}: {
  title: string
  setTitle: (v: string) => void
  description: string
  setDescription: (v: string) => void
  cover: CoverDraft
  setCover: (c: CoverDraft) => void
  onNext: () => void
  /**
   * Whether the studio is the screen in front of the creator.
   *
   * A stack on this client keeps the screen it came from mounted, so leaving the
   * studio does *not* unmount it — which meant the cover film carried on playing
   * to an empty room, and was still doing it when the creator came back. Nothing
   * about a screen nobody is looking at should be running: this gates the only
   * thing in the step that makes a sound.
   */
  active: boolean
  /**
   * Test-only: the length of a film the picker would have returned (see
   * `FlashBookFixtures`).
   *
   * It goes through `applyPickedFilm` — the same door a real pick goes through —
   * rather than seeding the cover directly, so what the browser measures is the
   * shipping path and not a stand-in for it.
   */
  seededFilmMs?: number
}) {
  const fb = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const [picking, setPicking] = useState(false)
  /** The film whose minute is being chosen. Nothing is committed until then. */
  const [trimFilm, setTrimFilm] = useState<PickedMedia | null>(null)

  /**
   * One action, whatever is in the creator's library.
   *
   * The step used to ask *which kind of cover* first — a row of pills saying
   * Image / Image + audio / Video — and only then open a picker that had been
   * narrowed to match. That is a question a creator cannot answer before they
   * have looked at what they have, asked twice: once as a mode, once as a file.
   * So the picker opens on pictures *and* films, and the app works out what came
   * back (see `coverPickKind`).
   *
   * A film longer than a cover may run is not refused — it opens the trimmer, so
   * the creator chooses the minute the book starts on instead of being sent away
   * to cut it somewhere else. That is also why the picker is no longer asked for
   * `videoMaxDuration`: it used to cut the pick at the cap, which is now the one
   * thing that must not happen.
   */
  const pickCover = async () => {
    setPicking(true)
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images', 'videos'],
        quality: 1,
      })
      const asset = result.canceled ? undefined : result.assets[0]
      if (!asset) return
      if (coverPickKind(asset) === 'video') {
        applyPickedFilm({
          uri: asset.uri,
          durationMs: asset.duration ?? undefined,
        })
        return
      }
      // A new picture keeps the narration: swapping the artwork is not a reason
      // to make the creator record their introduction again.
      setCover({
        image: {uri: asset.uri},
        audio: cover.audio,
        video: null,
        videoStartMs: undefined,
      })
    } finally {
      setPicking(false)
    }
  }

  /**
   * A picked film, as the cover — or as a question, when it runs over.
   *
   * A film that fits *is* the cover and brings its own sound, so there is nothing
   * left for a picture or a narration to do. A film that does not fit becomes a
   * candidate and the trimmer opens over the step; cancelling leaves the cover
   * exactly as it was, because a creator who closes a question has answered
   * nothing.
   */
  const applyPickedFilm = (film: PickedMedia) => {
    const next = coverFromPickedFilm(film)
    if (!next) {
      setTrimFilm(film)
      return
    }
    setCover(next)
  }

  /*
   * The fixture's film, through the door a real pick goes through.
   *
   * Keyed on the film's *length* rather than on a film object, and the length is
   * a number for a reason: an object built in the parent's render is a new value
   * every time the parent renders, so this ran on every render and re-opened the
   * trimmer the moment it was closed — the fixture could pick a minute, apply it,
   * and be asked again before it could draw the result. The seed is a length, so
   * re-rendering cannot re-seed. It calls `coverFromPickedFilm` rather than
   * `applyPickedFilm` because that keeps this effect's dependencies to values the
   * compiler can see are stable; a function in the list would be memoized by hand
   * and the seed would depend on that being remembered.
   */
  useEffect(() => {
    if (!seededFilmMs) return
    const film = {uri: FIXTURE_COVER_FILM_URI, durationMs: seededFilmMs}
    const next = coverFromPickedFilm(film)
    if (next) setCover(next)
    else setTrimFilm(film)
  }, [seededFilmMs, setCover])

  /** The art on the stage: a film if there is one, the picture otherwise. */
  const coverPreviewUri = cover.video?.uri ?? cover.image?.uri
  const isVideo = !!cover.video
  const canNext = title.trim().length > 0 && !!coverPreviewUri

  /**
   * Whether the cover film's minute is still the creator's to choose.
   *
   * Only when its length is known: a film loaded from a draft carries the window
   * it was saved with but not the length, and a trimmer whose ruler has no numbers
   * on it is worse than no trimmer.
   */
  const canTrim = isVideo && coverNeedsTrim(cover.video?.durationMs)

  /**
   * Whether there is anything *behind* the overlaid text.
   *
   * This is the one branch in the cover step that has to be a branch: with art,
   * the title sits on the creator's own picture and takes white ink over a
   * scrim; without art there is nothing to fight and it is ordinary studio ink
   * on a studio surface. Baking either one in would make the other unreadable.
   */
  const hasArt = !!coverPreviewUri

  return (
    <View style={fb.coverStage}>
      {hasArt ? (
        <>
          {/* The creator's own cover, and nothing on top of it but the words. */}
          {isVideo ? (
            <StudioWindowVideo
              uri={coverPreviewUri}
              startMs={cover.videoStartMs}
              /*
               * Silent, and held still while the trimmer is open.
               *
               * Both of those were the opposite, and both were wrong in a way a
               * creator could hear. The stage used to autoplay *unmuted*, so
               * picking a film started talking over whatever they were doing; and
               * it kept playing underneath the trimmer, which shows the same film
               * in its own preview — two copies of one soundtrack, a beat apart.
               * Sound belongs to the trimmer, where it is a choice with a button
               * beside it, and the stage stays a picture.
               */
              playing={active && !trimFilm}
              muted
              showControls={false}
              style={StyleSheet.absoluteFill}
            />
          ) : (
            <Image
              source={{uri: coverPreviewUri}}
              style={StyleSheet.absoluteFill}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          )}

          {/* Scrims: keep the overlaid text readable on any image. */}
          <LinearGradient
            pointerEvents="none"
            colors={[OVER_ART.scrimFrom, OVER_ART.scrimClear]}
            style={fb.coverTopScrim}
          />
          <LinearGradient
            pointerEvents="none"
            colors={OVER_ART.titleScrim}
            locations={[0, 0.45, 1]}
            style={fb.coverBottomScrim}
          />
        </>
      ) : (
        /*
         * Nothing picked yet, so the stage is the invitation: what a cover can
         * be, then the one button that starts it. Both options are named before
         * the creator is asked to choose, because the choice is about what is
         * already in their library — a question a mode selector cannot ask.
         */
        <View style={fb.coverEmpty} testID="flashBookCoverEmpty">
          <View style={fb.coverEmptyIcons}>
            <Feather name="image" size={20} color={c.accent} />
            <Text style={[fb.coverEmptyOr, {color: c.textFaint}]}>{l`or`}</Text>
            <Feather name="video" size={20} color={c.accent} />
          </View>
          <Text style={fb.coverEmptyTitle}>{l`Add a cover`}</Text>
          <Text style={fb.coverEmptyTxt}>
            {l`A photo, with your own voice-over if you want one — or a video. A cover plays for ${COVER_VIDEO_MAX_SECONDS} seconds, and you choose which ones.`}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Choose a cover`}
            accessibilityHint={l`Picks a photo or a video from your library`}
            disabled={picking}
            onPress={() => void pickCover()}
            testID="flashBookCoverAddBtn"
            style={[fb.coverAddBtn, {backgroundColor: c.accentFill}]}>
            {picking ? (
              <ActivityIndicator size="small" color={c.onAccent} />
            ) : (
              <Feather name="upload" size={14} color={c.onAccent} />
            )}
            <Text style={[fb.coverAddBtnTxt, {color: c.onAccent}]}>
              {l`Photo or video`}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/*
        Changing the cover — and the only control over the art.

        The stage is deliberately *not* a tap target here: it is the creator's
        own picture, and a tap while looking at it should not open their photo
        library. One labelled button, one action.
      */}
      {hasArt && (
        <View style={[fb.coverTop, fb.coverTopRow]} pointerEvents="box-none">
          {/*
            The minute, for a film that runs over. It sits beside `Change` rather
            than inside it because they are different questions — *this film, a
            different minute* and *a different film* — and a creator who liked
            what they shot but chose the wrong opening should not have to find it
            again in their library.
          */}
          {canTrim && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Choose the minute the cover opens on`}
              accessibilityHint={l`Opens the trimmer for this film`}
              onPress={() => setTrimFilm(cover.video)}
              testID="flashBookCoverTrimBtn"
              style={[fb.coverTab, fb.coverTabOnArt]}>
              <Feather name="scissors" size={13} color={OVER_ART.inkStrong} />
              <Text style={[fb.coverTabTxt, {color: OVER_ART.inkStrong}]}>
                {cover.videoStartMs ? l`Trimmed` : l`Trim`}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Change cover`}
            accessibilityHint={l`Picks another photo or video`}
            disabled={picking}
            onPress={() => void pickCover()}
            testID="flashBookCoverChangeBtn"
            style={[fb.coverTab, fb.coverTabOnArt]}>
            {picking ? (
              <ActivityIndicator size="small" color={OVER_ART.inkStrong} />
            ) : (
              <Feather name="refresh-cw" size={13} color={OVER_ART.inkStrong} />
            )}
            <Text style={[fb.coverTabTxt, {color: OVER_ART.inkStrong}]}>
              {l`Change`}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Title & description sit on the cover, exactly as learners will see them */}
      <View style={fb.coverBottom}>
        <StudioTextField
          accessibilityLabel={l`Book title`}
          accessibilityHint={l`The name learners will see`}
          style={[fb.coverTitleInput, hasArt && fb.coverTitleOnArt]}
          value={title}
          onChangeText={setTitle}
          placeholder={l`Book title…`}
          placeholderTextColor={hasArt ? OVER_ART.inkFaint : c.textFaint}
          textAlignVertical="top"
        />
        <StudioTextField
          accessibilityLabel={l`Book description`}
          accessibilityHint={l`A short summary of the book`}
          style={[fb.coverDescInput, hasArt && fb.coverDescOnArt]}
          value={description}
          onChangeText={setDescription}
          placeholder={l`Short description…`}
          placeholderTextColor={hasArt ? OVER_ART.inkFaint : c.textFaint}
          minLines={2}
          textAlignVertical="top"
        />

        {/*
          The voice-over, offered the moment there is a picture to speak over.

          It arrives *after* the artwork instead of being a mode chosen before
          it, and never over a film: a video brings its own sound, and a second
          soundtrack over it is not something anyone asked for. The field itself
          is a reading block's — record on the spot, upload a file, or polish a
          take in the desk — so the creator hears what the learner will.

          It carries no sentence of its own: the invitation before the button
          already said a photo can have a voice-over, and the field's own label
          already says it is optional. Two explanations of one optional thing is
          how a screen starts feeling like a form.
        */}
        {!isVideo && !!cover.image && (
          <View
            testID="flashBookCoverNarration"
            style={[fb.coverAudioPills, hasArt && fb.coverAudioPillsOnArt]}>
            <FlashBookAudioField
              url={cover.audio?.uri}
              accent={c.accentFill}
              label={l`VOICE-OVER`}
              onChange={uri => setCover({...cover, audio: uri ? {uri} : null})}
            />
          </View>
        )}

        {/* Why Continue is not available yet, named for whichever part is
            actually missing — "add a title and a cover" one at a time. */}
        {!canNext && (
          <Text style={[fb.coverReqTxt, hasArt && fb.coverReqOnArt]}>
            {!title.trim() && !coverPreviewUri
              ? l`Add a title and a cover to continue`
              : !title.trim()
                ? l`Add a title to continue`
                : l`Add a cover to continue`}
          </Text>
        )}

        <TouchableOpacity
          accessibilityRole="button"
          accessibilityHint={
            canNext
              ? l`Continues to the theme step`
              : l`Add what the message above asks for first`
          }
          disabled={!canNext}
          onPress={onNext}
          style={[fb.coverContinue, {opacity: canNext ? 1 : 0.5}]}>
          <Text style={fb.coverContinueTxt}>{l`Continue`}</Text>
          <Feather name="arrow-right" size={18} color={c.onAccent} />
        </TouchableOpacity>
      </View>

      {/*
        The trimmer, while a film's minute is being chosen.

        Mounted only while there is a candidate, so its state is fresh each time
        it opens and the studio's own cover stays untouched until it is answered:
        the film is not committed by picking it, only by choosing a window.
      */}
      {trimFilm && (
        <StudioCoverTrimSheet
          film={trimFilm}
          startMs={cover.videoStartMs ?? 0}
          accent={c.accentFill}
          onApply={startMs => {
            setCover({
              image: null,
              audio: null,
              video: trimFilm,
              videoStartMs: startMs,
            })
            setTrimFilm(null)
          }}
          onCancel={() => setTrimFilm(null)}
        />
      )}
    </View>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────

/**
 * Whether this build can measure loudness at all.
 *
 * Asked once, like the desk asks it, because the answer is a property of the
 * platform: the web has the Web Audio API, a phone has it through the native
 * engine, and a build with neither has no business offering to match a book.
 */
const LEVEL_MATCH_AVAILABLE = canEditAudio()

type Props = NativeStackScreenProps<CommonNavigatorParams, 'FlashBookStudio'>

export function FlashBookStudioScreen({navigation, route}: Props) {
  const fb = useStyles()
  const c = useStudioChrome()
  const {t: l, i18n} = useLingui()
  const agent = useAgent()
  /*
   * Whether this screen is the one in front of the creator.
   *
   * Not the same as "is it mounted": a stack keeps the screen it pushed from
   * mounted, so this stays true for the whole visit and turns false the moment
   * another screen takes over — which is the only signal a film has to stop on
   * (see `CoverStep.active`).
   */
  const isFocused = useIsFocused()
  const themeIdParam = route.params?.themeId
  const htmlThemeUrlParam = route.params?.htmlThemeUrl
  const fixtureParam = route.params?.fixture
  const fixturePageParam = route.params?.pageIndex
  const fixtureAudioParam = route.params?.audio
  const pickedFilmMsParam = route.params?.pickedFilmMs
  /**
   * The classroom this studio was opened from, when it was opened from one.
   *
   * A book begun in a room's Flashbooks tab is begun *for* that room, so the
   * publish below has somewhere to put it and somewhere to return to. Every other
   * way into the studio leaves this undefined, and the studio behaves exactly as it
   * always has. The capsule studio carries the same param for the same reason, and
   * the two are deliberately the same shape.
   */
  const classroomIdParam = route.params?.classroomId
  const {data: classroomRoom} = useClassroomQuery(classroomIdParam)
  const classroom = classroomRoom?.classroom
  // The server says whether this account may put work in the room.
  const forClassroom = !!classroomRoom?.viewer.canPost
  const addClassroomContent = useAddClassroomContentMutation(
    classroom?.id ?? '',
  )

  /**
   * The room's own year, when the room has one the records can carry.
   *
   * A book written inside a classroom belongs to that classroom, so it is
   * published at the classroom's level; the publisher shows the room's answers
   * and does not let them be cleared (`lockedLevel` in `PublishFlashBookDialog`,
   * the same rule the capsule studio follows).
   */
  const classroomLevel =
    classroom?.grade && categoryOfLevel(classroom.grade)
      ? classroom.grade
      : undefined

  /**
   * A draft to open on arrival: one the creator tapped on their own profile.
   *
   * A draft is the one kind of work whose whole purpose is to be continued, so the
   * profile's grid hands its cell this address and the studio does the rest — it
   * reads drafts by address anyway (see `loadDraft`). Absent for every other way in,
   * including the drafts list, which lives inside a studio that is already open.
   */
  const draftUriParam = route.params?.draftUri

  /**
   * Test-only: the browser-driven layout tests (e2e-web/) open this screen with
   * `?fixture=layout` to measure the real block cards against a book that has
   * every block type. Dev builds only, like the route that passes the param.
   *
   * `?audio=1` is the same book with a recorded reading on its reading blocks,
   * because a card with audio is the one that overflows the editor's bound and
   * the suites have to measure that case, not only the cards that fit.
   * `?audio=broken` is the same book with a take whose host does not resolve, so
   * the desk's unreadable-clip path is measurable too.
   * `?audio=levels` is the same book with real readings at two very different
   * levels, which is what the level matcher is measured against.
   */
  const fixtureBook =
    IS_DEV && fixtureParam
      ? fixtureAudioParam === 'levels'
        ? withLevelledReadings(buildFlashBookFixture(themeIdParam))
        : fixtureAudioParam
          ? withReadingAudio(
              buildFlashBookFixture(themeIdParam),
              fixtureAudioParam === 'broken'
                ? FIXTURE_UNREADABLE_AUDIO
                : undefined,
            )
          : buildFlashBookFixture(themeIdParam)
      : null

  const [step, setStep] = useState(fixtureBook ? 2 : 0)
  const [title, setTitle] = useState(fixtureBook?.title ?? '')
  const [description, setDescription] = useState(fixtureBook?.description ?? '')
  const [cover, setCover] = useState<CoverDraft>({
    image: fixtureBook?.cover.imageUri
      ? {uri: fixtureBook.cover.imageUri}
      : null,
    audio: null,
    video: null,
  })
  const [selectedThemeId, setSelectedThemeId] = useState(
    fixtureBook?.themeId ?? themeIdParam ?? 'storybook',
  )
  const [selectedHtmlThemeUrl, setSelectedHtmlThemeUrl] = useState<
    string | undefined
  >(htmlThemeUrlParam ?? undefined)
  const [pages, setPages] = useState<FlashBookPage[]>(() =>
    fixtureBook ? fixtureBook.pages : [{id: nanoid(8), title: '', blocks: []}],
  )
  const [currentPage, setCurrentPage] = useState(
    fixtureBook ? (fixturePageParam ?? 0) : 0,
  )
  const [showBlockPicker, setShowBlockPicker] = useState(false)
  const [showThemePicker, setShowThemePicker] = useState(false)
  /**
   * The bed, held apart from the theme.
   *
   * `undefined` — nothing ever chosen — is a real state and the default, because
   * it is what every book written before the catalogue existed holds: they keep
   * the bed their theme has always played. `null` is the creator taking the bed
   * away on purpose, which is why it is not the same value.
   */
  const [backgroundMusicId, setBackgroundMusicId] =
    useState<BackgroundMusicChoice>(() =>
      fixtureBook
        ? musicChoiceFromRecord(fixtureBook.backgroundMusicId)
        : undefined,
    )
  const [showMusicPicker, setShowMusicPicker] = useState(false)
  const [preview, setPreview] = useState<{
    visible: boolean
    mode: 'book' | 'page'
    pageIdx: number
  }>({visible: false, mode: 'book', pageIdx: 0})
  /** Block currently previewed in place, inside its own card (null = none). */
  const [previewBlockId, setPreviewBlockId] = useState<string | null>(null)
  /**
   * The block that blocked the last publish attempt.
   *
   * Validation is a *publish-time* concern: printing it under every unfinished
   * block the whole time the creator is writing reads as nagging, and the note
   * sits in the same place on every card, so it stops carrying any signal. It
   * is raised here instead — on the one block that actually stopped the
   * publish — and clears itself the moment that block is fixed.
   */
  const [issueBlockId, setIssueBlockId] = useState<string | null>(null)
  /**
   * A block to bring into view as soon as it has a measured position.
   *
   * Two things leave the creator looking at the wrong part of the page: adding a
   * block, which lands at the end — usually below the fold — and a publish
   * refused by a block on a page they are not looking at. Both are revealed from
   * the block's own `onLayout` rather than from a scroll issued alongside the
   * edit, because a block that has only just been created has no recorded offset
   * to scroll to: its position is not known until it has laid out.
   */
  const pendingRevealRef = useRef<string | null>(null)
  /**
   * The block whose first field still owes the creator the caret.
   *
   * State, where the reveal above is a ref, because this one decides what is
   * *rendered*: the new block's card is the one that offers its first field the
   * caret (see `#/components/studio/StudioFirstFieldFocus`), and a decision about
   * the render cannot live in a ref. It is cleared the moment the block has had
   * its chance — whether or not a field was there to take it.
   */
  const [focusBlockId, setFocusBlockId] = useState<string | null>(null)
  const settledBlockFocus = useCallback(() => setFocusBlockId(null), [])
  /** Measured y of each block inside the current page stack. */
  const blockOffsetsRef = useRef<Record<string, number>>({})
  const pageStackRef = useRef<ScrollView>(null)

  // A copy is kept on this device as the book is written (see `autosave`), but
  // only a draft or a publish makes it real — so the guard below still needs to
  // know what the creator would be leaving behind.
  /**
   * Drafts, newest first: the server's list by author, merged with this device's
   * index. Which of the two is in front of the creator — and what happens when
   * the server cannot be asked at all — is the whole of `useFlashBookDrafts`.
   */
  const {drafts, setDrafts} = useFlashBookDrafts(agent.session?.did)
  const queryClient = useQueryClient()
  /** Draft the current work was loaded from, if any. */
  const [loadedDraftUri, setLoadedDraftUri] = useState<string | null>(null)
  /**
   * That draft's own `createdAt`. Publishing converts the draft record in
   * place, and the record is replaced wholesale, so the original date has to
   * travel with it or the book would look published-when-published instead of
   * created-when-created.
   */
  const [loadedDraftCreatedAt, setLoadedDraftCreatedAt] = useState<
    string | null
  >(null)
  /** Draft being fetched by the drafts list (shows a spinner on its row). */
  const [openingDraftUri, setOpeningDraftUri] = useState<string | null>(null)
  /** What the unsaved-work guard is currently protecting. */
  const [leaveIntent, setLeaveIntent] = useState<
    {kind: 'leave'} | {kind: 'replace'; draftTitle: string}
  >({kind: 'leave'})
  /** A draft save is in flight — the leave dialog stays open until it lands. */
  const [savingDraft, setSavingDraft] = useState(false)
  /** Fingerprint of the book as it was last saved (draft or published). */
  const [savedSignature, setSavedSignature] = useState<string | null>(null)
  /**
   * The copy of this book left behind by the session that ended before this one.
   *
   * Read once, at open, and held for the life of the screen. The autosave rewrites
   * the device's slot within a second of the first edit, so a copy re-read later
   * would be gone by the time it mattered — and the moment it matters most is when
   * a draft is opened, which is exactly when a book's unfinished work lives.
   */
  const [lastSession] = useState<FlashBookSnapshot | null>(() =>
    getFlashBookSnapshot(),
  )
  /** Set right before we let a guarded navigation through (see below). */
  const allowLeaveRef = useRef(false)
  /** What to do once the leave dialog is resolved. */
  const pendingLeaveRef = useRef<null | (() => void)>(null)
  const leaveControl = useDialogControl()
  const draftsControl = useDialogControl()
  const deleteControl = useDialogControl()
  const levelControl = useDialogControl()
  const recoveryControl = useDialogControl()
  /**
   * What a destructive press would remove, held until the creator confirms.
   *
   * Deleting a block or a page is the one edit the recovery copy cannot undo —
   * it keeps the book as it is *now*, not as it was — so a mis-tap on the trash
   * icon would still be final. This is the stop between the tap and the loss.
   */
  const [pendingDelete, setPendingDelete] = useState<
    | {
        kind: 'block'
        pageId: string
        id: string
        title: string
        message: string
      }
    | {kind: 'page'; id: string; title: string; message: string}
    | {kind: 'draft'; id: string; title: string; message: string}
    | null
  >(null)

  /** Leaving a page always ends any in-place block preview. */
  const goToPage = useCallback((index: number) => {
    setPreviewBlockId(null)
    setCurrentPage(index)
  }, [])

  const nav = useNavigation<NavigationProp>()
  const pagerScrollRef = useRef<ScrollView>(null)

  const {width: windowWidth, height: windowHeight} = useWindowDimensions()
  const {top: safeAreaTop, bottom: safeAreaBottom} = useSafeAreaInsets()
  const {gtMobile} = useBreakpoints()
  // The studio fills the web shell's centre column, at every breakpoint.
  //
  // The studio renders inside that column (the `Layout.Center` wrapper below),
  // which is the full window up to the `gtMobile` breakpoint and
  // `CENTER_COLUMN_WIDTH` above it. Measuring the *window* instead would do two
  // things wrong: let the studio spill out of the column, and centre it on the
  // viewport — the wrong axis, because between 1100 and 1300 the shell shifts the
  // column by `CENTER_COLUMN_OFFSET` (-105px), so a viewport-centred studio ends
  // up sitting 105px to the right of the column it is supposed to live in.
  const columnWidth = gtMobile ? Layout.CENTER_COLUMN_WIDTH : windowWidth
  // The studio's canvas is the column, edge to edge, and the window's height —
  // the same behaviour as the capsule studio beside it, so the two creators feel
  // like one product: chrome at the top, canvas filling the rest.
  //
  // The width used to be derived from a 9:16 canvas instead, which made the
  // *window height* decide it: on a 1440×800 desktop the book stayed 394px wide
  // inside a 600px column, with a dead gutter either side, and never filled the
  // column the rest of the app is laid out in.
  const availW = Math.max(240, columnWidth)
  const availH = Math.max(0, windowHeight - safeAreaTop - safeAreaBottom)
  const frameWidth = Math.round(availW)
  const frameHeight = Math.round(Math.max(320, availH))

  useEffect(() => {
    return () => {
      stopReadingAudio()
    }
  }, [])

  const themeColor = getFlashbookTheme(selectedThemeId).previewColor

  const previewModel: FlashBookModel = useMemo(() => {
    const coverForPreview: FlashBookCover = {
      kind: coverKindOf(cover),
      imageUri: cover.image?.uri,
      audioUri: cover.audio?.uri,
      videoUri: cover.video?.uri,
    }
    return {
      id: 'preview',
      title: title || l`Untitled book`,
      description,
      creatorId: '',
      themeId: selectedThemeId,
      backgroundMusicId: musicChoiceToRecord(backgroundMusicId),
      status: 'draft',
      cover: coverForPreview,
      pages,
      behaviorSettings: selectedHtmlThemeUrl
        ? {htmlThemeUrl: selectedHtmlThemeUrl}
        : {},
      metadata: {createdAt: '', updatedAt: ''},
      likeCount: 0,
      likedByMe: false,
    }
  }, [
    backgroundMusicId,
    cover,
    description,
    pages,
    selectedHtmlThemeUrl,
    selectedThemeId,
    title,
    l,
  ])

  const canPublish = useMemo(() => {
    if (!title.trim()) return false
    if (pages.length === 0) return false
    if (!cover.image && !cover.video) return false
    return pages.every(p =>
      p.blocks.every(
        b => validateSlide(b, m => String(m.id ?? m.message ?? '')) === null,
      ),
    )
  }, [title, pages, cover])

  const publishControl = useDialogControl()

  /** What a refused publish found, as the issues sheet lists it. */
  const [publishIssues, setPublishIssues] = useState<StudioIssue[]>([])
  const publishIssuesControl = useDialogControl()

  /**
   * Takes the creator to a problem the publish was refused for.
   *
   * The issue sheet says *what* is wrong and where it is; this is the half that
   * puts them in front of it. A block is ringed and revealed from its own
   * measured position, exactly as a block that has just been added is — and on
   * another page the turn happens first, with the block revealed as that page
   * lays out (see `pendingRevealRef`).
   */
  const openIssue = useCallback(
    (pageIndex: number, blockId?: string) => {
      // The ring belongs to the block: a problem with a page as a whole has
      // nothing to ring, and clearing it here stops the last one lingering.
      setIssueBlockId(blockId ?? null)
      if (pageIndex !== currentPage) {
        if (blockId) pendingRevealRef.current = blockId
        goToPage(pageIndex)
        return
      }
      if (!blockId) return
      const y = blockOffsetsRef.current[blockId]
      if (y != null) {
        pageStackRef.current?.scrollTo({y: Math.max(0, y - 16), animated: true})
      }
    },
    [currentPage, goToPage],
  )

  /**
   * Everything a publish would be refused for, in the order the book is read.
   *
   * The cover first, then page by page. A page with nothing on it is a problem in
   * its own right — a reader turning to it finds a blank sheet — and its empty
   * row is offered only when the page is not already the one on screen, because a
   * row that would jump nowhere is not a link.
   *
   * This is the list, not the first item of it: a creator finishing a book is
   * editing, and being told about one empty block at a time turns finishing it
   * into a walk of its own.
   */
  const collectPublishIssues = useCallback((): StudioIssue[] => {
    const issues: StudioIssue[] = []

    if (!title.trim()) {
      issues.push({
        key: 'book:title',
        where: l`Cover`,
        what: l`Title`,
        message: l`Give the book a title.`,
        onOpen: () => setStep(0),
      })
    }
    if (!cover.image && !cover.video) {
      issues.push({
        key: 'book:cover',
        where: l`Cover`,
        what: l`Cover art`,
        message: l`Add a picture or a short video.`,
        onOpen: () => setStep(0),
      })
    }
    if (pages.length === 0) {
      issues.push({
        key: 'book:pages',
        where: l`Book`,
        what: l`Pages`,
        message: l`Add a page to write on.`,
      })
    }

    for (let pi = 0; pi < pages.length; pi++) {
      const page = pages[pi]
      if (page.blocks.length === 0) {
        issues.push({
          key: `page:${pi}`,
          where: l`Page ${pi + 1}`,
          what: l`Empty page`,
          message: l`Add a block to this page, or delete the page.`,
          ...(pi !== currentPage ? {onOpen: () => openIssue(pi)} : null),
        })
      }
      for (const block of page.blocks) {
        const problem = validateSlide(block, l)
        if (!problem) continue
        issues.push({
          key: block.id,
          where: l`Page ${pi + 1}`,
          what: blockLabelOf(block, l),
          message: problem,
          onOpen: () => openIssue(pi, block.id),
        })
      }
    }

    return issues
  }, [cover, currentPage, l, openIssue, pages, title])

  const handlePublish = useCallback(() => {
    const issues = collectPublishIssues()
    if (issues.length > 0) {
      // Not a toast per problem: one sheet holding every one of them, so the
      // creator can see the shape of the work left to do and pick where to start.
      setPublishIssues(issues)
      publishIssuesControl.open()
      return
    }
    // Publishable: whatever the last attempt flagged is water under the bridge.
    setIssueBlockId(null)
    publishControl.open()
  }, [collectPublishIssues, publishControl, publishIssuesControl])

  // ── Unsaved work ──────────────────────────────────────────────────────────

  const blockCount = useMemo(
    () => pages.reduce((acc, page) => acc + page.blocks.length, 0),
    [pages],
  )

  const contentSignature = flashBookContentSignature({
    title,
    description,
    themeId: selectedThemeId,
    backgroundMusicId,
    htmlThemeUrl: selectedHtmlThemeUrl,
    cover: coverSignatureOf(cover),
    pages,
  })

  /**
   * Anything the creator typed, picked or built counts as unsaved work.
   *
   * A copy is being kept on this device as they go (see `autosave` below), so
   * leaving is no longer the catastrophe it was — but the copy is *this device's*,
   * and the draft is what makes the book real. Work that matches the last save (an
   * untouched draft) does not count.
   */
  const hasUnsavedWork =
    contentSignature !== savedSignature &&
    bookHasContent({title, description, cover: coverRecordOf(cover), pages})

  /**
   * The book the studio started from, for deciding whether a copy on the device
   * is *this* book's unfinished work.
   *
   * It moves when a draft is opened, because that is when the studio learns which
   * book it is holding. Deliberately not `savedSignature`, which moves on every
   * save: the question here is "was this copy taken from the book I am holding?",
   * not "has anything changed since I last saved?".
   */
  const [baseline, setBaseline] = useState<{
    draftUri: string | null
    signature: string
  }>(() => ({draftUri: null, signature: contentSignature}))
  /** Set once the offer has been answered, either way, so it cannot reappear. */
  const [recoveryAnswered, setRecoveryAnswered] = useState(false)

  /**
   * The copy worth handing back, if there is one.
   *
   * Derived rather than stored, so every way the question can change — a draft
   * opened, the offer answered — is one input to one expression, and there is no
   * path that leaves a stale offer on the screen.
   */
  const recoveryOffer = useMemo(
    () => (recoveryAnswered ? null : offerableSnapshot(lastSession, baseline)),
    [baseline, lastSession, recoveryAnswered],
  )

  /** The book as this device should keep it, read at the moment it is written. */
  const bookForRecovery = useCallback(
    (): FlashBookRecoveredBook => ({
      title,
      description,
      themeId: selectedThemeId,
      backgroundMusicId: musicChoiceToRecord(backgroundMusicId),
      htmlThemeUrl: selectedHtmlThemeUrl,
      cover: coverRecordOf(cover),
      pages,
    }),
    [
      backgroundMusicId,
      cover,
      description,
      pages,
      selectedHtmlThemeUrl,
      selectedThemeId,
      title,
    ],
  )

  /**
   * Whether this session has written anything worth keeping.
   *
   * Deliberately narrower than `hasUnsavedWork`, which is the *leave guard's*
   * question — "is there anything that would be lost?" — and is already true the
   * moment a book is loaded, because a loaded book differs from "never saved".
   * This one asks what the autosave needs to know: has anything changed since this
   * screen opened? A session that is only reading what was already there has
   * nothing to keep, and — the part that matters — must not write over the copy of
   * the *previous* session's work that it is offering back.
   */
  const hasWorkThisSession =
    hasUnsavedWork && contentSignature !== baseline.signature

  /**
   * The copy of this book on this device, kept as it is written.
   *
   * It writes to the device and nothing else. Saving a draft stays the creator's
   * decision — that is the whole design of the drafts feature — and this is the app
   * remembering for them in the meantime. The snapshot is cleared the moment the
   * book matches a save — and the moment the work stops being this session's, if
   * the creator answers the recovery offer with *start fresh*.
   */
  const autosave = useFlashBookAutosave({
    signature: contentSignature,
    keep: hasWorkThisSession,
    draftUri: loadedDraftUri,
    take: bookForRecovery,
  })

  /**
   * The offer is a decision, so it arrives as a dialog rather than a banner that
   * could be scrolled past and forgotten.
   *
   * It asks again after a navigation `state` event, and that second asking is not
   * belt-and-braces — it is the only reason the offer is ever seen on the web. The
   * shell dismisses every open dialog on each of those events
   * (`view/shell/index.web.tsx`), including the one that brings this screen in, so
   * the dialog opened on mount is swept away before it has been painted: the offer
   * is computed correctly and never shown.
   *
   * Measured rather than assumed. With the offer standing, the count of restore
   * buttons in the DOM read 0 right after mount, still 0 in the same task as the
   * navigation event even though the handler had just opened it, 0 at 300 ms and
   * 800 ms — and 1 within a frame of opening it on a later task. Hence the
   * `setTimeout`: only *after* the sweep has finished does the asking survive, and
   * opening in the handler is undone by the very event being handled. To a creator
   * this is a frame, not a delay.
   *
   * The effect is torn down the moment the creator answers, either way, so nothing
   * can re-open a question that has been settled. A dismissal that is not an answer
   * (the backdrop, Escape) leaves the offer standing, which is the honest reading of
   * it: the creator has not said whether the copy is theirs.
   */
  useEffect(() => {
    if (!recoveryOffer) return
    recoveryControl.open()
    let pending: ReturnType<typeof setTimeout> | null = null
    const sub = navigation.addListener('state', () => {
      if (pending) return
      pending = setTimeout(() => {
        pending = null
        recoveryControl.open()
      }, 0)
    })
    return () => {
      if (pending) clearTimeout(pending)
      sub()
    }
  }, [navigation, recoveryControl, recoveryOffer])

  const exitStudio = useCallback(() => {
    if (navigation.canGoBack?.()) {
      navigation.goBack()
      return
    }
    nav.navigate('Home')
  }, [navigation, nav])

  /**
   * Every way out of the studio funnels through here: with unpublished work it
   * asks first (save a draft or discard), otherwise it just leaves.
   *
   * The pending thunk flags the exit as allowed, so re-issuing it after the
   * dialog closes doesn't trip the `beforeRemove` guard a second time.
   */
  const requestLeave = useCallback(
    (action: () => void) => {
      if (!hasUnsavedWork) {
        action()
        return
      }
      setLeaveIntent({kind: 'leave'})
      pendingLeaveRef.current = () => {
        allowLeaveRef.current = true
        action()
      }
      leaveControl.open()
    },
    [hasUnsavedWork, leaveControl],
  )

  // Swipe-back, browser back and hardware back never reach the header button,
  // so the guard has to listen for them too — otherwise the warning would only
  // cover the one exit the creator is least likely to use.
  useEffect(() => {
    const sub = navigation.addListener('beforeRemove', e => {
      if (allowLeaveRef.current || !hasUnsavedWork) return
      e.preventDefault()
      setLeaveIntent({kind: 'leave'})
      pendingLeaveRef.current = () => {
        allowLeaveRef.current = true
        navigation.dispatch(e.data.action)
      }
      leaveControl.open()
    })
    return sub
  }, [hasUnsavedWork, leaveControl, navigation])

  /** Resolves the guard by running whatever the dialog was protecting. */
  const resolvePending = useCallback(() => {
    const action = pendingLeaveRef.current
    pendingLeaveRef.current = null
    // Closing runs the pending action after the dialog has gone, so the creator
    // never sees the studio flash back to life behind the overlay.
    if (action) {
      leaveControl.close(action)
    } else {
      leaveControl.close()
    }
  }, [leaveControl])

  /**
   * Puts the copy from the previous session back.
   *
   * Both fields the studio needs to go on saving correctly are already right: the
   * offer is only ever made against the book the studio is holding, so a copy that
   * came from a draft record arrives after that record was loaded, and saving
   * afterwards updates it rather than leaving a second half-finished book behind.
   */
  const applyRecovery = useCallback(() => {
    const snapshot = recoveryOffer
    if (!snapshot) return
    const {book} = snapshot
    setTitle(book.title)
    setDescription(book.description)
    setSelectedThemeId(book.themeId)
    setBackgroundMusicId(musicChoiceFromRecord(book.backgroundMusicId))
    setSelectedHtmlThemeUrl(book.htmlThemeUrl)
    setCover(coverDraftFrom(book.cover))
    setPages(
      book.pages.length > 0
        ? book.pages
        : [{id: nanoid(8), title: '', blocks: []}],
    )
    setCurrentPage(0)
    setPreviewBlockId(null)
    // Straight to the pages: the book is written, and the cover step would ask the
    // creator to answer again something they answered last session.
    setStep(2)
    setRecoveryAnswered(true)
    Toast.show(l`Picked up where you left off.`)
  }, [l, recoveryOffer])

  /**
   * Throws the copy away and starts clean — the creator's answer, not a default.
   *
   * The device's copy goes with it, because a copy nobody wants is a dialog
   * everybody meets twice.
   */
  const discardRecovery = useCallback(() => {
    setRecoveryAnswered(true)
    autosave.discard()
  }, [autosave])

  /**
   * Keeps the book on the creator's PDS as a `status: 'draft'` record — no post,
   * so it stays private — and remembers it locally so the drafts list can offer
   * it back.
   */
  const handleSaveDraft = useCallback(async () => {
    const did = agent.session?.did
    if (!did) {
      Toast.show(
        l`You need to be signed in to save a draft.`,
        'circle-exclamation',
      )
      return
    }
    setSavingDraft(true)
    try {
      const {
        uri,
        createdAt,
        cover: savedCover,
      } = await saveFlashBookDraft({
        agent,
        i18n,
        did,
        title,
        description,
        themeId: selectedThemeId,
        backgroundMusicId: musicChoiceToRecord(backgroundMusicId),
        htmlThemeUrl: selectedHtmlThemeUrl,
        cover: coverRecordOf(cover),
        pages,
        // Saving again during the same session updates the record the first
        // save created, rather than leaving a trail of near-identical drafts.
        draftUri: loadedDraftUri,
        createdAt: loadedDraftCreatedAt,
      })
      setLoadedDraftUri(uri)
      setLoadedDraftCreatedAt(createdAt)
      setDrafts(
        rememberFlashBookDraft({
          uri,
          title: title.trim() || l`Untitled book`,
          pageCount: pages.length,
          updatedAt: new Date().toISOString(),
          // The *uploaded* cover, not the picks: a row shows this book's own
          // artwork, and a `blob:` URL outlives neither a reload nor the tab it
          // was made in (see `SavedFlashBookRef.cover`).
          cover: flashBookDraftCoverOf(savedCover),
        }),
      )
      // The server list is the authority now, so it has to hear about this save.
      void queryClient.invalidateQueries({
        queryKey: createActorFlashbookDraftsQueryKey(did),
      })
      setSavedSignature(contentSignature)
      Toast.show(l`Draft saved — find it under “Drafts”.`)
      resolvePending()
    } catch (e) {
      console.error('Failed to save flashbook draft', e)
      Toast.show(l`Could not save the draft.`, 'circle-exclamation')
    } finally {
      setSavingDraft(false)
    }
  }, [
    agent,
    contentSignature,
    cover,
    description,
    i18n,
    l,
    loadedDraftCreatedAt,
    loadedDraftUri,
    pages,
    queryClient,
    resolvePending,
    selectedHtmlThemeUrl,
    selectedThemeId,
    // Stable (it is the `useState` setter inside `useFlashBookDrafts`), but the
    // rule cannot see through a custom hook, so it is named here.
    setDrafts,
    title,
  ])

  /**
   * Drops the changes — including this device's copy of them.
   *
   * A draft this work came from is left untouched: discarding means the *unsaved*
   * work goes, and a recovery copy is exactly that. Leaving it behind would mean
   * the studio offering back the evening the creator just threw away.
   */
  const handleDiscard = useCallback(() => {
    autosave.discard()
    resolvePending()
  }, [autosave, resolvePending])

  /**
   * Pulls a saved draft back into the studio so it can be finished.
   *
   * It takes the record's *address* rather than a drafts-list row, because a draft
   * can also arrive from outside the studio altogether: a creator's own profile draws
   * their unfinished work beside their published work, and that cell opens the draft
   * here (see `draftUriParam`). Everything a row carried was decoration; the address
   * is what the read needs.
   *
   * The list stays *open* while this runs and is closed by the load itself, once
   * the book is in the studio. It used to be the other way round — the tap closed
   * the dialog and the fetch started in the close callback — which meant the row's
   * spinner could never be seen: by the time the studio knew which draft was
   * loading, the row that said so was gone. The wait belongs on the row that was
   * tapped, and it only ends where the creator can see the result.
   */
  const loadDraft = useCallback(
    async (draftUri: string) => {
      const did = agent.session?.did
      if (!did) {
        // A tap that does nothing and says nothing is a dead button. In the app
        // this cannot happen — the studio is behind `requireAuth` — but a drafts
        // row that silently ignores a press is not a state worth leaving in.
        Toast.show(l`Could not open this draft.`, 'circle-exclamation')
        return
      }
      setOpeningDraftUri(draftUri)
      try {
        const atUri = new AtUri(draftUri)
        const res = await agent.com.atproto.repo.getRecord({
          repo: atUri.hostname,
          collection: atUri.collection,
          rkey: atUri.rkey,
        })
        const value = res.data.value as Record<string, unknown>
        const savedCover = value.cover as FlashBookCover | undefined
        const savedPages = (value.pages as FlashBookPage[] | undefined) ?? []
        const htmlThemeUrl = (
          value.behaviorSettings as {htmlThemeUrl?: string} | undefined
        )?.htmlThemeUrl

        const nextTitle = (value.title as string | undefined) ?? ''
        const nextDescription = (value.description as string | undefined) ?? ''
        const nextThemeId = (value.themeId as string | undefined) ?? 'storybook'
        // In the record's spelling here, so the loaded signature below matches
        // the one the saved book had: an opened draft is not unsaved work.
        const nextBackgroundMusicId = musicChoiceFromRecord(
          value.backgroundMusicId,
        )
        const nextCover: CoverDraft = coverDraftFrom(savedCover)
        const nextPages =
          savedPages.length > 0
            ? savedPages
            : [{id: nanoid(8), title: '', blocks: []}]

        setTitle(nextTitle)
        setDescription(nextDescription)
        setSelectedThemeId(nextThemeId)
        setBackgroundMusicId(nextBackgroundMusicId)
        setSelectedHtmlThemeUrl(htmlThemeUrl)
        setCover(nextCover)
        setPages(nextPages)
        // The book now matches what is on the server, so leaving it untouched
        // must not read as unsaved work.
        const loadedSignature = flashBookContentSignature({
          title: nextTitle,
          description: nextDescription,
          themeId: nextThemeId,
          backgroundMusicId: nextBackgroundMusicId,
          htmlThemeUrl,
          cover: coverSignatureOf(nextCover),
          pages: nextPages,
        })
        setSavedSignature(loadedSignature)
        // And this is the book the studio is holding, which is the only moment it
        // can learn that a copy on the device belongs to *this* draft.
        setBaseline({draftUri, signature: loadedSignature})
        setCurrentPage(0)
        setPreviewBlockId(null)
        setLoadedDraftUri(draftUri)
        setLoadedDraftCreatedAt(
          typeof value.createdAt === 'string' ? value.createdAt : null,
        )
        // Land straight in the editor: the whole point of a draft is to finish
        // the pages, not to re-answer the cover step.
        setStep(2)
        draftsControl.close()
        Toast.show(l`Draft loaded.`)
      } catch (e) {
        console.error('Failed to open flashbook draft', e)
        Toast.show(l`Could not open this draft.`, 'circle-exclamation')
      } finally {
        setOpeningDraftUri(null)
      }
    },
    [agent, draftsControl, l],
  )

  /**
   * Opening a draft replaces whatever is in the studio, so when there is
   * unsaved work it goes through the same guard as leaving — where the creator
   * can save the current book first.
   *
   * With nothing to lose, the list stays up and shows the row working; the load
   * closes it when the book is in the studio. (With unsaved work the list has to
   * go first — the leave dialog is the next thing the creator has to answer, and
   * two dialogs cannot be asked at once.)
   */
  const handleOpenDraft = useCallback(
    (draft: FlashBookDraftRef) => {
      if (!hasUnsavedWork) {
        void loadDraft(draft.uri)
        return
      }
      setLeaveIntent({kind: 'replace', draftTitle: draft.title})
      draftsControl.close(() => {
        pendingLeaveRef.current = () => void loadDraft(draft.uri)
        leaveControl.open()
      })
    },
    [draftsControl, hasUnsavedWork, leaveControl, loadDraft],
  )

  /**
   * A draft named by the route, opened once when the studio arrives.
   *
   * Once, and at mount, because there is nothing here to lose yet: the studio the
   * creator came from is a *profile*, so this session begins empty and the whole
   * unsaved-work path — the guard `handleOpenDraft` has to go through — is about a
   * studio that is already holding somebody's work. Guarded by a ref rather than by
   * the param, since the effect must not run a second time when React re-runs it.
   */
  const openedParamDraft = useRef(false)
  useEffect(() => {
    if (!draftUriParam || openedParamDraft.current) return
    openedParamDraft.current = true
    void loadDraft(draftUriParam)
  }, [draftUriParam, loadDraft])

  /**
   * Deleting a draft is irreversible and now reaches every device the creator
   * uses, so it asks first — and says so, because the older behaviour only hid
   * the row here.
   */
  const handleRemoveDraft = useCallback(
    (draft: FlashBookDraftRef) => {
      draftsControl.close(() => {
        setPendingDelete({
          kind: 'draft',
          id: draft.uri,
          title: l`Delete this draft?`,
          message: l`“${draft.title}” will be deleted from your drafts everywhere, not just on this device. This cannot be undone.`,
        })
        deleteControl.open()
      })
    },
    [deleteControl, draftsControl, l],
  )

  /**
   * Back is never a dead end. Inside the wizard it steps back (editor → theme →
   * cover); once there is no earlier step it leaves the screen, falling back to
   * Home when the studio was opened without history (deep link, or right after
   * publishing), where `canGoBack()` returns false and the press used to do
   * nothing at all.
   */
  const handleBack = useCallback(() => {
    if (step > 0) {
      setStep(step - 1)
      return
    }
    requestLeave(exitStudio)
  }, [requestLeave, exitStudio, step])

  // ── Page operations ─────────────────────────────────────────────────────────

  const addPage = useCallback(() => {
    setPages(prev => [...prev, {id: nanoid(8), title: '', blocks: []}])
    setPreviewBlockId(null)
    setCurrentPage(pages.length)
  }, [pages.length])

  const deletePage = useCallback(
    (pageId: string) => {
      if (pages.length <= 1) return
      setPages(prev => prev.filter(p => p.id !== pageId))
      setPreviewBlockId(null)
      setCurrentPage(p => Math.max(0, p - 1))
    },
    [pages.length],
  )

  const movePage = useCallback(
    (dir: -1 | 1) => {
      const i = currentPage
      const next = i + dir
      if (next < 0 || next >= pages.length) return
      setPages(prev => {
        const arr = [...prev]
        ;[arr[i], arr[next]] = [arr[next], arr[i]]
        return arr
      })
      setPreviewBlockId(null)
      setCurrentPage(next)
    },
    [currentPage, pages.length],
  )

  const updatePageTitle = useCallback((pageId: string, t: string) => {
    setPages(prev => prev.map(p => (p.id === pageId ? {...p, title: t} : p)))
  }, [])

  const addBlock = useCallback(
    (type: SlideType, patch?: Partial<Slide>) => {
      // `patch` is how a rich reading block arrives already knowing it is a
      // quote / note / table, instead of being created plain and re-rendered.
      const block = createSlide(type, patch)
      setPages(prev =>
        prev.map(p =>
          p.id === pages[currentPage].id
            ? {...p, blocks: [...p.blocks, block]}
            : p,
        ),
      )
      // A new block is appended to the page, so on a full page it is created out
      // of sight. It is revealed the moment it has laid out: the creator asked
      // for this block, so the block — not the place it was appended to — is what
      // they should be looking at.
      pendingRevealRef.current = block.id
      // And it is handed to them ready to write in: the creator picked a block
      // type in order to put something in it, so the caret goes into the block's
      // own first field rather than waiting for them to find and tap it.
      setFocusBlockId(block.id)
    },
    [currentPage, pages],
  )

  const updateBlock = useCallback(
    (pageId: string, blockId: string, data: Partial<Slide>) => {
      setPages(prev =>
        prev.map(p =>
          p.id === pageId
            ? {
                ...p,
                blocks: p.blocks.map(b =>
                  b.id === blockId ? {...b, ...data} : b,
                ),
              }
            : p,
        ),
      )
    },
    [],
  )

  // ── Matching every page's reading level ─────────────────────────────────────

  /**
   * The clips a match would work on — the cover's introduction, then each page.
   *
   * Also the gate on the header action: with fewer than two readings there is
   * nothing for a level to be *matched to*, and an action that can only ever say
   * "nothing to do" is noise on a crowded header.
   */
  const readingClipCount = useMemo(
    () => readingClipsOf(pages, cover.audio?.uri).length,
    [cover, pages],
  )

  /**
   * Makes a rewritten clip durable, then writes it into the book.
   *
   * The upload happens here rather than at save time so a matched page is an
   * asset the moment it is rematched — the same rule the block's own audio field
   * follows, and for the same reason: a rewrite that lives only in this tab goes
   * silent for every reader, and for its author on the next device.
   */
  const handleLevelled = useCallback(
    async (levelled: LevelledReadingClip[]) => {
      const did = agent.session?.did
      const resolved = new Map<string, string>()
      for (const entry of levelled) {
        let url = entry.url
        if (did) {
          try {
            const asset = await uploadClip({agent, did, uri: entry.url})
            rememberLocalClip(asset, entry.url)
            url = asset
          } catch {
            // Local is still a working clip, and the save-time sweep will send
            // it — the pass must not fail a page over a flaky upload.
          }
        }
        resolved.set(entry.clip.id, url)
      }
      setPages(prev =>
        prev.map(page => ({
          ...page,
          blocks: page.blocks.map(block => {
            const next = resolved.get(block.id)
            return next ? {...block, readingAudioUrl: next} : block
          }),
        })),
      )
      const intro = resolved.get(COVER_CLIP_ID)
      if (intro) {
        setCover(prev => (prev.audio ? {...prev, audio: {uri: intro}} : prev))
      }
    },
    [agent],
  )

  /** Puts the recordings back, for the dialog's undo. */
  const handleLevelledUndo = useCallback(
    (clips: {id: string; url: string}[]) => {
      const byId = new Map(clips.map(clip => [clip.id, clip.url]))
      setPages(prev =>
        prev.map(page => ({
          ...page,
          blocks: page.blocks.map(block => {
            const original = byId.get(block.id)
            return original ? {...block, readingAudioUrl: original} : block
          }),
        })),
      )
      const intro = byId.get(COVER_CLIP_ID)
      if (intro) {
        setCover(prev => (prev.audio ? {...prev, audio: {uri: intro}} : prev))
      }
    },
    [],
  )

  const {
    state: levelState,
    start: startLevelMatch,
    apply: applyLevelMatch,
    undo: undoLevelMatch,
    reset: resetLevelMatch,
  } = useFlashBookLevelMatch({
    pages,
    coverAudioUri: cover.audio?.uri,
    onLevelled: handleLevelled,
    onRestore: handleLevelledUndo,
  })

  /**
   * Opens the dialog on a fresh measurement.
   *
   * Measuring starts with the open rather than from an effect inside the dialog,
   * because the dialog is mounted whether or not it is visible — an effect there
   * would run a book-wide decode on screen load.
   */
  const openLevelMatch = useCallback(() => {
    resetLevelMatch()
    startLevelMatch()
    levelControl.open()
  }, [levelControl, resetLevelMatch, startLevelMatch])

  // The reset is deferred to after the close animation, so the dialog never
  // repaints back to its first phase on the way out.
  const closeLevelMatch = useCallback(() => {
    levelControl.close(() => resetLevelMatch())
  }, [levelControl, resetLevelMatch])

  const deleteBlock = useCallback(
    (pageId: string, blockId: string) => {
      setPages(prev =>
        prev.map(p =>
          p.id === pageId
            ? {...p, blocks: p.blocks.filter(b => b.id !== blockId)}
            : p,
        ),
      )
      if (issueBlockId === blockId) setIssueBlockId(null)
    },
    [issueBlockId],
  )

  /**
   * Deletes a draft for real.
   *
   * Hiding the row was enough while the list lived on this device. Now that the
   * list is read from the server, a local-only removal would simply be refetched
   * and reappear — and the drafts on the creator's other devices would keep it
   * forever.
   */
  const deleteDraft = useCallback(
    async (uri: string) => {
      const did = agent.session?.did
      if (!did) return
      try {
        const atUri = new AtUri(uri)
        await agent.com.atproto.repo.deleteRecord({
          repo: atUri.hostname,
          collection: atUri.collection,
          rkey: atUri.rkey,
        })
      } catch (e) {
        console.error('Failed to delete flashbook draft', e)
        Toast.show(l`Could not delete this draft.`, 'circle-exclamation')
        return
      }
      setDrafts(forgetFlashBookDraft(uri))
      if (loadedDraftUri === uri) {
        setLoadedDraftUri(null)
        setLoadedDraftCreatedAt(null)
      }
      // Take it out of the cached list too, so the row does not blink back into
      // existence if React Query refetches before the indexer has caught up.
      queryClient.setQueryData(
        createActorFlashbookDraftsQueryKey(did),
        (prev: FlashBookDraftRef[] | undefined) =>
          (prev ?? []).filter(d => d.uri !== uri),
      )
    },
    [agent, l, loadedDraftUri, queryClient, setDrafts],
  )

  /**
   * One trash icon, one question. Every destructive press funnels through here
   * so the wording can say what actually disappears — a block's content, a whole
   * page and the blocks on it, or a draft on every device.
   */
  const confirmPendingDelete = useCallback(() => {
    const pending = pendingDelete
    setPendingDelete(null)
    if (!pending) return
    if (pending.kind === 'block') {
      deleteBlock(pending.pageId, pending.id)
    } else if (pending.kind === 'page') {
      deletePage(pending.id)
    } else {
      void deleteDraft(pending.id)
    }
  }, [deleteBlock, deleteDraft, deletePage, pendingDelete])

  const moveBlock = useCallback(
    (pageId: string, blockId: string, dir: -1 | 1) => {
      setPages(prev =>
        prev.map(p => {
          if (p.id !== pageId) return p
          const i = p.blocks.findIndex(b => b.id === blockId)
          const j = i + dir
          if (i < 0 || j < 0 || j >= p.blocks.length) return p
          const arr = [...p.blocks]
          ;[arr[i], arr[j]] = [arr[j], arr[i]]
          return {...p, blocks: arr}
        }),
      )
    },
    [],
  )

  const duplicateBlock = useCallback((pageId: string, blockId: string) => {
    setPages(prev =>
      prev.map(p => {
        if (p.id !== pageId) return p
        const i = p.blocks.findIndex(b => b.id === blockId)
        if (i < 0) return p
        const copy = {...p.blocks[i], id: nanoid(8)}
        const arr = [...p.blocks]
        arr.splice(i + 1, 0, copy)
        return {...p, blocks: arr}
      }),
    )
  }, [])

  // ── Theme step (rendered inside the shared phone frame below) ──────────────
  const themeStepContent = (
    <FlashBookThemeSelectorView
      showSafeAreaTop={false}
      onBack={() => setStep(0)}
      onSelect={(themeId, htmlThemeUrl) => {
        setSelectedThemeId(themeId)
        setSelectedHtmlThemeUrl(htmlThemeUrl)
        setStep(2)
      }}
    />
  )

  // ── Cover step (rendered inside the shared phone frame below) ──────────────
  const coverStepContent = (
    <>
      <View style={fb.header}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Back`}
          accessibilityHint={l`Leaves the book creator`}
          onPress={handleBack}
          style={fb.backBtn}>
          <Feather name="arrow-left" size={20} color={c.text} />
        </TouchableOpacity>
        <Text style={fb.headerTitle}>{l`New FlashBook`}</Text>
        {drafts.length > 0 && (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Unpublished drafts`}
            accessibilityHint={l`Opens your saved drafts`}
            onPress={() => draftsControl.open()}
            testID="flashBookDraftsBtn"
            style={fb.draftsBtn}>
            <Feather name="bookmark" size={12} color={c.text} />
            <Text style={fb.draftsBtnTxt}>{l`Drafts (${drafts.length})`}</Text>
          </TouchableOpacity>
        )}
      </View>
      <CoverStep
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        cover={cover}
        setCover={setCover}
        onNext={() => setStep(1)}
        active={isFocused}
        seededFilmMs={IS_DEV ? pickedFilmMsParam : undefined}
      />
    </>
  )

  // ── Page editor step ────────────────────────────────────────────────────────
  const currentPageObj = pages[currentPage]
  const currentTheme = getFlashbookTheme(selectedThemeId)
  // Same design language the reader will render this block with, so an
  // in-place studio preview is an honest preview of the finished book.
  const currentDesign = getFlashbookDesign(currentTheme.id)

  return (
    <Layout.Screen minimalShell testID="flashbookStudioScreen">
      <FlashBookPreviewModal
        visible={preview.visible}
        book={previewModel}
        initialPageIndex={preview.mode === 'page' ? preview.pageIdx : 0}
        isSinglePage={preview.mode === 'page'}
        // A page preview jumps straight into the content; the whole-book
        // preview shows the cover first.
        startInReading={preview.mode !== 'book'}
        onClose={() => setPreview(p => ({...p, visible: false}))}
      />
      <PublishFlashBookDialog
        control={publishControl}
        title={title}
        description={description}
        cover={coverRecordOf(cover)}
        pages={pages}
        themeId={selectedThemeId}
        backgroundMusicId={musicChoiceToRecord(backgroundMusicId)}
        htmlThemeUrl={selectedHtmlThemeUrl}
        draftUri={loadedDraftUri}
        draftCreatedAt={loadedDraftCreatedAt}
        // Only a studio opened from a room asks who the book is for (see the
        // dialog), and only somebody who may put work in that room is offered it.
        classroomName={forClassroom ? classroom?.name : undefined}
        lockedLevel={classroomLevel}
        onSuccess={published => {
          // Published — there is nothing left to warn about, and if this work
          // came from a draft the draft has served its purpose.
          allowLeaveRef.current = true
          // Nor is there anything left to recover: the book is on the record now,
          // and a copy on the device would only offer the creator their own
          // published book back.
          autosave.discard()
          if (loadedDraftUri) setDrafts(forgetFlashBookDraft(loadedDraftUri))
          /*
            A book published from a room goes into that room.

            The creator opened the studio from the classroom's own Flashbooks
            tab, so making them find their new book in a picker afterwards — and
            dropping them on Home in the meantime — would be the mistake the
            capsule studio already stopped making. The room keeps the book, and
            the creator is returned to the room, on the tab they left: the studio
            was pushed on top of the classroom, not instead of it.

            Which room entry it becomes is the creator's answer on the last step,
            and the two differ in who the book is for, not in what was written:

            • **Published** — a public book: the post goes on the profile and into
              the feeds, and the room shows the same post the rest of the app does.
              Sharing it into a classroom does not duplicate it.
            • **This classroom only** — the same two records, marked as the room's
              own (see `CLASSROOM_ONLY_POST`). It is a real post with everything a
              post has — replies, reactions, the menu — and the app keeps it out of
              profiles, feeds and shelf.

            The entry carries the audience as its own `visibility`; the
            server decides who sees it (`listContent`).
          */
          if (classroom && forClassroom) {
            const roomName = classroom.name
            addClassroomContent
              .mutateAsync({
                classroomId: classroom.id,
                kind: 'flashbook',
                visibility: published.forClassroom ? 'members' : 'public',
                text: description,
                flashbookTitle: title,
                postUri: published.postUri,
                postCid: published.postCid,
                flashbookUri: published.flashbookUri,
                flashbookCid: published.flashbookCid,
              })
              .then(() =>
                Toast.show(
                  published.forClassroom
                    ? l`FlashBook is only in ${roomName}`
                    : l`FlashBook added to ${roomName}`,
                ),
              )
              .catch(error => showClassroomError(i18n, error))
          }
          /*
           * Back to where the work was begun: the room it was written for, or the
           * creator's own profile when one of its work tabs sent them here. Only
           * when there is somewhere to go back to — a studio opened by URL is the
           * first screen in the stack, and the fallback below is what that has
           * always done (see the same shape in `CapsuleStudio`).
           */
          if ((classroom || draftUriParam) && navigation.canGoBack()) {
            navigation.goBack()
            return
          }
          if (navigation.canGoBack()) navigation.popToTop()
          nav.navigate('Home')
        }}
      />
      <StudioIssuesDialog
        control={publishIssuesControl}
        title={l`This book isn't ready to publish`}
        caption={
          publishIssues.length === 1
            ? l`One thing needs fixing before a reader can open this book. Nothing has been published.`
            : l`${publishIssues.length} things need fixing before a reader can open this book. Nothing has been published.`
        }
        issues={publishIssues}
      />
      <FlashBookLeaveDialog
        control={leaveControl}
        busy={savingDraft}
        unsavedBlocks={blockCount}
        intent={leaveIntent.kind}
        draftTitle={
          leaveIntent.kind === 'replace' ? leaveIntent.draftTitle : undefined
        }
        onSaveDraft={() => void handleSaveDraft()}
        onDiscard={handleDiscard}
      />
      <FlashBookDraftsDialog
        control={draftsControl}
        drafts={drafts}
        openingUri={openingDraftUri}
        onOpen={draft => void handleOpenDraft(draft)}
        onRemove={handleRemoveDraft}
      />
      <StudioConfirmDialog
        control={deleteControl}
        title={pendingDelete?.title ?? ''}
        message={pendingDelete?.message ?? ''}
        confirmLabel={
          pendingDelete?.kind === 'page'
            ? l`Delete page`
            : pendingDelete?.kind === 'draft'
              ? l`Delete draft`
              : l`Delete block`
        }
        onConfirm={confirmPendingDelete}
      />
      <FlashBookLevelMatchDialog
        control={levelControl}
        state={levelState}
        onApply={applyLevelMatch}
        onUndo={undoLevelMatch}
        onClose={closeLevelMatch}
      />
      {/*
        The copy from the session that ended before this one.

        Mounted whether or not there is one — a dialog control can only open a
        dialog that is on the screen — and it renders as nothing at all when there
        is no offer, which is nearly always.
      */}
      <FlashBookRecoveryDialog
        control={recoveryControl}
        snapshot={recoveryOffer}
        onRestore={applyRecovery}
        onDiscard={discardRecovery}
      />
      <View style={fb.screenWrap}>
        <Layout.Center style={fb.frameColumn}>
          <View
            style={[fb.studioFrame, {width: frameWidth, height: frameHeight}]}>
            {/*
              Whether the book is being kept, under the header in both of the
              steps a book is written in.

              Not on the theme step — that one is a full-screen chooser with no
              header to sit under, and nothing is being typed while it is open.
            */}
            {step !== 1 && <FlashBookAutosaveNote state={autosave.state} />}
            {step === 1 ? (
              themeStepContent
            ) : step === 0 ? (
              coverStepContent
            ) : (
              <>
                {/* Custom creator header */}
                <View style={fb.header}>
                  <HintButton
                    label={step > 1 ? l`Back to theme` : l`Back`}
                    hint={
                      step > 1
                        ? l`Returns to the theme step`
                        : l`Leaves the book creator`
                    }
                    onPress={handleBack}
                    style={fb.backBtn}>
                    <Feather name="arrow-left" size={20} color={c.text} />
                  </HintButton>

                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => setStep(0)}
                    style={fb.titleBtn}>
                    <Text style={fb.titleTxt} numberOfLines={1}>
                      {title || l`Untitled book`}
                    </Text>
                    <Feather name="edit-2" size={11} color={c.textFaint} />
                  </TouchableOpacity>

                  {/* Theme picker — palette icon tinted with the active theme */}
                  <HintButton
                    label={l`Theme`}
                    hint={l`Opens the theme picker`}
                    testID="flashbookThemeBtn"
                    onPress={() => setShowThemePicker(true)}
                    style={[
                      fb.iconPill,
                      {
                        borderColor: themeColor + '66',
                        backgroundColor: themeColor + '1f',
                      },
                    ]}>
                    <Ionicons
                      name="color-palette"
                      size={17}
                      color={themeColor}
                    />
                  </HintButton>

                  {/*
                    Background music — the theme's neighbour, and its own
                    setting.

                    Deliberately beside the palette rather than inside it: the
                    creator's next question after "how does this book look" is
                    "what does it sound like", and a control that states the
                    answer is better than one that hides it. A crossed-out
                    speaker is the one state a creator cannot infer by looking.
                  */}
                  <HintButton
                    label={l`Background music`}
                    hint={
                      backgroundMusicId === null
                        ? l`No music plays under the pages — opens the music browser`
                        : l`Opens the music browser`
                    }
                    onPress={() => setShowMusicPicker(true)}
                    testID="flashbookMusicBtn"
                    style={[
                      fb.iconPill,
                      backgroundMusicId != null
                        ? {
                            borderColor: c.accent + '66',
                            backgroundColor: c.accent + '1f',
                          }
                        : null,
                    ]}>
                    <Feather
                      name={backgroundMusicId === null ? 'volume-x' : 'music'}
                      size={16}
                      color={backgroundMusicId != null ? c.accent : c.text}
                    />
                  </HintButton>

                  {/* Read the whole book, from the cover — book icon only */}
                  <HintButton
                    label={l`Preview book`}
                    hint={l`Reads the whole book from the cover`}
                    disabled={!pages.some(p => p.blocks.length > 0)}
                    onPress={() =>
                      setPreview({
                        visible: true,
                        mode: 'book',
                        pageIdx: 0,
                      })
                    }
                    style={fb.iconPill}>
                    <Feather name="book-open" size={16} color={c.text} />
                  </HintButton>

                  {/*
                    Match the reading levels — a book-wide tool, so it lives in
                    the book's header rather than on a page.

                    Only offered when the book has at least two readings and this
                    build can measure them: a level needs something to be matched
                    *against*, and a control that can only ever report "nothing to
                    do" is worse than no control at all.
                  */}
                  {LEVEL_MATCH_AVAILABLE && readingClipCount >= 2 && (
                    <HintButton
                      label={l`Match the reading levels`}
                      hint={l`Brings every page's reading to the same level`}
                      onPress={openLevelMatch}
                      testID="flashbookLevelMatchBtn"
                      style={fb.iconPill}>
                      <Feather name="bar-chart-2" size={16} color={c.text} />
                    </HintButton>
                  )}

                  {/* Publish */}
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityHint={l`Checks the whole book, then opens publishing`}
                    testID="flashbookPublishBtn"
                    onPress={handlePublish}
                    style={[fb.publishBtn, !canPublish && {opacity: 0.5}]}>
                    <Text style={fb.publishTxt}>{l`Publish`}</Text>
                  </TouchableOpacity>
                </View>

                {/*
                  The page area: one scroller, holding the page itself.

                  The sheet is as wide as the centre column and grows with its
                  blocks, and the studio's own chrome — header above, page chips
                  below — stays put while it scrolls between them. The anchored
                  controls hang off *this* wrapper rather than off the sheet, so
                  "add block" and the page actions never scroll out of reach.
                */}
                <View style={a.flex_1}>
                  <ScrollView
                    ref={pageStackRef}
                    style={a.flex_1}
                    contentContainerStyle={fb.pageScroll}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}>
                    <View testID="flashbookStudioPage" style={fb.pageSheet}>
                      <View style={fb.pageStack}>
                        {/* Page title input */}
                        <TextInput
                          accessibilityLabel={l`Page title`}
                          accessibilityHint={l`Optional heading for this page`}
                          style={fb.pageTitleInput}
                          value={currentPageObj?.title ?? ''}
                          onChangeText={v =>
                            updatePageTitle(currentPageObj?.id ?? '', v)
                          }
                          placeholder={l`Page title (optional)…`}
                          placeholderTextColor={c.textFaint}
                        />

                        {(currentPageObj?.blocks ?? []).map((block, bi) => {
                          const richKind =
                            block.type === 'content'
                              ? (block.contentKind ?? 'text')
                              : undefined
                          const meta = SLIDE_TYPE_META[block.type]
                          // A block type's colour is drawn as a label and a
                          // border on the card, so it is tuned for the card's
                          // surface before it is used (see `readableAccent`).
                          const accent = readableAccent(
                            (richKind
                              ? CONTENT_KIND_ACCENT[richKind]
                              : undefined) ??
                              meta?.color ??
                              c.accentFill,
                            c,
                          )
                          // The resting border, which is this card's whole shape:
                          // the tint is laid on the chrome's structural hairline
                          // rather than on the page, so a pale hue still reads as
                          // an edge (see `cardBorder`).
                          const blockLabel = blockLabelOf(block, l)
                          const isPreviewing = previewBlockId === block.id
                          // Only the block that blocked the last publish carries a
                          // note (see `handlePublish`) — and it does so only while
                          // it is still the thing standing in the way, so fixing it
                          // clears the note and the ring together.
                          const blockIssue =
                            !isPreviewing && issueBlockId === block.id
                              ? validateSlide(block, l)
                              : null
                          const isFlagged = !!blockIssue
                          return (
                            <View
                              key={block.id}
                              // Keyed by the full block identity, so a rich reading
                              // block is `content:table` rather than a second
                              // `content` on the card list.
                              testID={`flashbook-block--${slideBlockKey(block)}`}
                              onLayout={e => {
                                const y = e.nativeEvent.layout.y
                                blockOffsetsRef.current[block.id] = y
                                /*
                                 * Bring the block the creator is owed a look at
                                 * — the one they just added, or the one that
                                 * stopped a publish — into view, from the
                                 * position it has just reported. The small
                                 * margin keeps the card's own header clear of
                                 * the scroller's top edge.
                                 */
                                if (pendingRevealRef.current !== block.id)
                                  return
                                pendingRevealRef.current = null
                                pageStackRef.current?.scrollTo({
                                  y: Math.max(0, y - 16),
                                  animated: true,
                                })
                              }}
                              style={[
                                fb.blockCard,
                                isFlagged && fb.blockCardFlagged,
                                {
                                  borderColor: isFlagged
                                    ? c.warning
                                    : isPreviewing
                                      ? accent
                                      : cardBorder(accent, c),
                                },
                              ]}>
                              {/* One header per block: the play action + the block type
                        label on the left, reorder/duplicate/delete on the
                        right (swapped for a close action while previewing).
                        The editor below hides its own duplicate header. */}
                              <View style={fb.blockChrome}>
                                <TouchableOpacity
                                  accessibilityRole="button"
                                  accessibilityLabel={
                                    isPreviewing
                                      ? l`Stop previewing this block`
                                      : l`Preview this block`
                                  }
                                  accessibilityHint={l`Plays this block in place`}
                                  onPress={() =>
                                    setPreviewBlockId(
                                      isPreviewing ? null : block.id,
                                    )
                                  }
                                  style={fb.blockChromeLeft}>
                                  <Feather
                                    name="play-circle"
                                    size={16}
                                    color={accent}
                                  />
                                  <Text
                                    numberOfLines={1}
                                    style={[
                                      fb.blockChromeLabel,
                                      {color: accent},
                                    ]}>
                                    {blockLabel}
                                  </Text>
                                </TouchableOpacity>
                                <View style={fb.blockActions}>
                                  {isPreviewing && (
                                    <TouchableOpacity
                                      accessibilityRole="button"
                                      accessibilityLabel={l`Close block preview`}
                                      accessibilityHint={l`Goes back to editing`}
                                      onPress={() => setPreviewBlockId(null)}
                                      hitSlop={6}
                                      style={fb.blockPreviewClose}>
                                      <Feather
                                        name="x"
                                        size={14}
                                        color={c.text}
                                      />
                                    </TouchableOpacity>
                                  )}
                                  {!isPreviewing && (
                                    <>
                                      <IconBtn
                                        icon="arrow-up"
                                        color={c.textMuted}
                                        onPress={() =>
                                          moveBlock(
                                            currentPageObj.id,
                                            block.id,
                                            -1,
                                          )
                                        }
                                        disabled={bi === 0}
                                        label={l`Move up`}
                                      />
                                      <IconBtn
                                        icon="arrow-down"
                                        color={c.textMuted}
                                        onPress={() =>
                                          moveBlock(
                                            currentPageObj.id,
                                            block.id,
                                            1,
                                          )
                                        }
                                        disabled={
                                          bi ===
                                          currentPageObj.blocks.length - 1
                                        }
                                        label={l`Move down`}
                                      />
                                      <IconBtn
                                        icon="copy"
                                        color={c.textMuted}
                                        onPress={() =>
                                          duplicateBlock(
                                            currentPageObj.id,
                                            block.id,
                                          )
                                        }
                                        label={l`Duplicate`}
                                      />
                                      <IconBtn
                                        icon="trash-2"
                                        color={c.danger}
                                        onPress={() => {
                                          setPendingDelete({
                                            kind: 'block',
                                            pageId: currentPageObj.id,
                                            id: block.id,
                                            title: l`Delete this block?`,
                                            message: l`This ${blockLabel} block and everything in it will be removed from page ${currentPage + 1}. This cannot be undone.`,
                                          })
                                          deleteControl.open()
                                        }}
                                        label={l`Delete block`}
                                      />
                                    </>
                                  )}
                                </View>
                              </View>
                              {/*
                            What stopped the last publish, stated on the block
                            itself. Only the block that blocked it carries this
                            line, and only until it is fixed — never a standing
                            warning on every unfinished card.
                          */}
                              {!!blockIssue && (
                                <View style={fb.blockIssueRow}>
                                  <Feather
                                    name="alert-triangle"
                                    size={12}
                                    color={c.warning}
                                  />
                                  <Text style={fb.blockIssueRowTxt}>
                                    {blockIssue}
                                  </Text>
                                </View>
                              )}
                              {/* Body: in-place preview of this block, or its editor */}
                              {isPreviewing ? (
                                <View
                                  style={[
                                    fb.blockPreviewBody,
                                    {
                                      backgroundColor:
                                        currentTheme.tokens.pageBg,
                                    },
                                  ]}>
                                  <FlashBookBlockPlayer
                                    block={block}
                                    tokens={currentTheme.tokens}
                                    design={currentDesign}
                                    isPreview
                                    onResult={() => {}}
                                  />
                                </View>
                              ) : (
                                /*
                                 * The editor is the card's one scrolling region.
                                 *
                                 * It used to be a plain box with a `maxHeight`,
                                 * inside a card that clips — which is a wall, not
                                 * a bound: an editor taller than the cap (a content
                                 * block with a recording, an open audio editor) was
                                 * simply cut off, and the block's audio band, painted
                                 * under it, cut across the field above it. Bounded and
                                 * scrollable is what the cap was always for, and the
                                 * band now scrolls with the block instead of being
                                 * pinned into the text.
                                 */
                                <ScrollView
                                  style={fb.blockEditor}
                                  contentContainerStyle={fb.blockEditorInner}
                                  keyboardShouldPersistTaps="handled"
                                  showsVerticalScrollIndicator={false}>
                                  {/*
                                    The block that has just been added offers its
                                    first field the caret. Only the editor is
                                    wrapped — never the in-place preview — so a
                                    block being played can never take the caret
                                    out of the creator's writing.
                                  */}
                                  <FirstFieldFocus
                                    active={focusBlockId === block.id}
                                    onSettled={settledBlockFocus}>
                                    <FlashBookBlockEditor
                                      block={block}
                                      accent={currentTheme.tokens.accent}
                                      onChange={data =>
                                        updateBlock(
                                          currentPageObj.id,
                                          block.id,
                                          data,
                                        )
                                      }
                                    />
                                  </FirstFieldFocus>
                                </ScrollView>
                              )}
                            </View>
                          )
                        })}

                        {currentPageObj?.blocks.length === 0 && (
                          <TouchableOpacity
                            accessibilityRole="button"
                            accessibilityLabel={l`Add first block`}
                            accessibilityHint={l`Opens the block picker`}
                            onPress={() => setShowBlockPicker(true)}
                            style={fb.emptyPage}>
                            <View style={fb.emptyIcon}>
                              <Feather name="plus" size={20} color={c.accent} />
                            </View>
                            <Text
                              style={
                                fb.emptyPageTitle
                              }>{l`This page is empty`}</Text>
                            <Text style={fb.emptyPageTxt}>
                              {l`Tap to add your first block.`}
                            </Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  </ScrollView>

                  {/* In-page add block button — pinned to the page area, so it
                      stays reachable while the page scrolls under it. */}
                  <View style={[a.absolute, {bottom: 10, right: 10}, a.z_50]}>
                    <PageIconButton
                      icon="plus"
                      label={l`Add block`}
                      hint={l`Opens the block picker`}
                      primary
                      onPress={() => setShowBlockPicker(true)}
                    />
                  </View>

                  {/* Page actions */}
                  <View
                    style={[
                      a.absolute,
                      {bottom: 10, left: 10},
                      a.z_50,
                      a.flex_row,
                      a.gap_xs,
                    ]}>
                    {(currentPageObj?.blocks.length ?? 0) > 0 && (
                      <PageIconButton
                        icon="eye"
                        label={l`Preview this page`}
                        hint={l`Opens the page you are editing in the reader`}
                        onPress={() =>
                          setPreview({
                            visible: true,
                            mode: 'page',
                            pageIdx: currentPage,
                          })
                        }
                      />
                    )}
                    {pages.length > 1 && (
                      <PageIconButton
                        icon="trash-2"
                        danger
                        label={l`Delete page`}
                        hint={l`Removes this page`}
                        onPress={() => {
                          const blocks = currentPageObj.blocks.length
                          setPendingDelete({
                            kind: 'page',
                            id: currentPageObj.id,
                            title: l`Delete this page?`,
                            message:
                              blocks === 0
                                ? l`Page ${currentPage + 1} will be removed. This cannot be undone.`
                                : blocks === 1
                                  ? l`Page ${currentPage + 1} and its block will be removed. This cannot be undone.`
                                  : l`Page ${currentPage + 1} and its ${blocks} blocks will be removed. This cannot be undone.`,
                          })
                          deleteControl.open()
                        }}
                      />
                    )}
                    {currentPage > 0 && (
                      <PageIconButton
                        icon="chevron-left"
                        label={l`Move page left`}
                        hint={l`Moves this page one step left`}
                        onPress={() => movePage(-1)}
                      />
                    )}
                    {currentPage < pages.length - 1 && (
                      <PageIconButton
                        icon="chevron-right"
                        label={l`Move page right`}
                        hint={l`Moves this page one step right`}
                        onPress={() => movePage(1)}
                      />
                    )}
                  </View>

                  {/* Block type picker: the shared sheet, one tap to add */}
                  {showBlockPicker && (
                    <StudioBlockPicker
                      title={l`Add a block`}
                      subtitle={l`Goes to the end of page ${currentPage + 1}`}
                      sections={BLOCK_PICKER_SECTIONS}
                      footnote={l`Use the arrows on a block to reorder it.`}
                      testID="flashbookBlockPicker"
                      onClose={() => setShowBlockPicker(false)}
                      onSelect={entry => {
                        addBlock(entry.type, entry.patch)
                        setShowBlockPicker(false)
                      }}
                    />
                  )}

                  {/*
                    Theme picker overlay.

                    It carries its own sheet chrome — handle, title and a close
                    button — because the selector inside renders no header when
                    it is embedded, and without one the only way out of this
                    sheet was to pick a theme. Cancel is not an option a creator
                    should have to hunt for.
                  */}
                  {showThemePicker && (
                    <View style={fb.themeSheet} testID="flashbookThemeSheet">
                      <View style={fb.sheetHandle} />
                      <View style={fb.sheetHeader}>
                        <View style={{flex: 1, minWidth: 0}}>
                          <Text style={fb.sheetTitle}>{l`Choose a theme`}</Text>
                          <Text style={fb.sheetSub}>
                            {l`A theme restyles every page a reader will see`}
                          </Text>
                        </View>
                        <TouchableOpacity
                          accessibilityRole="button"
                          accessibilityLabel={l`Close the theme picker`}
                          accessibilityHint={l`Keeps the current theme`}
                          onPress={() => setShowThemePicker(false)}
                          testID="flashbookThemeSheetClose"
                          style={fb.sheetClose}>
                          <Feather name="x" size={16} color={c.text} />
                        </TouchableOpacity>
                      </View>
                      <View style={{flex: 1, overflow: 'hidden'}}>
                        <FlashBookThemeSelectorView
                          hideHeader
                          onBack={() => setShowThemePicker(false)}
                          onSelect={(themeId, htmlThemeUrl) => {
                            setSelectedThemeId(themeId)
                            setSelectedHtmlThemeUrl(htmlThemeUrl)
                            setShowThemePicker(false)
                          }}
                          showSafeAreaTop={false}
                        />
                      </View>
                    </View>
                  )}

                  {/*
                    The music browser.

                    Rendered beside the theme picker rather than inside it,
                    because the two are independent settings: changing the theme
                    must not disturb the track, and picking a track must not
                    restyle a page.
                  */}
                  {showMusicPicker && (
                    <BackgroundMusicSheet
                      selected={backgroundMusicId}
                      onSelect={setBackgroundMusicId}
                      onClose={() => setShowMusicPicker(false)}
                      testID="flashbookMusicSheet"
                    />
                  )}
                </View>

                {/* Bottom: page chips + "add page" */}
                <View style={fb.pagerBar}>
                  <ScrollView
                    ref={pagerScrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={fb.pagerChips}>
                    {pages.map((p, i) => {
                      const selected = i === currentPage
                      return (
                        <TouchableOpacity
                          accessibilityRole="button"
                          accessibilityLabel={l`Go to page ${i + 1}`}
                          accessibilityHint={l`Switches to this page`}
                          key={p.id}
                          onPress={() => goToPage(i)}
                          onLayout={e => {
                            const x = e.nativeEvent.layout.x
                            if (i === currentPage) {
                              pagerScrollRef.current?.scrollTo({
                                x: Math.max(0, x - 12),
                                animated: true,
                              })
                            }
                          }}
                          style={[fb.pageChip, selected && fb.pageChipActive]}>
                          <Text
                            style={[
                              fb.pageChipNum,
                              selected && fb.pageChipNumActive,
                            ]}>
                            {i + 1}
                          </Text>
                          <Text
                            numberOfLines={1}
                            style={[
                              fb.pageChipTxt,
                              selected && fb.pageChipTxtActive,
                            ]}>
                            {p.title?.trim() || l`Page`}
                          </Text>
                        </TouchableOpacity>
                      )
                    })}
                  </ScrollView>
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l`Add page`}
                    accessibilityHint={l`Appends a new empty page`}
                    onPress={addPage}
                    style={fb.addPageBtn}>
                    <Feather name="plus" size={15} color={c.onAccent} />
                    <Text style={fb.addPageTxt}>{l`Add page`}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </Layout.Center>
      </View>
    </Layout.Screen>
  )
}

/**
 * A creator control whose glyph is the whole story.
 *
 * The studio's chrome is icon-only by design, so every such control carries an
 * accessible name and hint for assistive tech.
 */
function HintButton({
  label,
  hint,
  onPress,
  disabled,
  style,
  testID,
  children,
}: {
  /** The control's accessible name. */
  label: string
  /** What the control does, for screen readers. */
  hint: string
  onPress?: () => void
  disabled?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
  children: React.ReactNode
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={hint}
      disabled={disabled}
      onPress={onPress}
      testID={testID}
      style={({pressed}) => [
        style,
        {opacity: disabled ? 0.4 : pressed ? 0.55 : 1},
      ]}>
      {children}
    </Pressable>
  )
}

/**
 * Small circular control that floats over the book canvas. Kept in the
 * studio's own dark palette so it never reads as an alien white button.
 */
function PageIconButton({
  icon,
  label,
  hint,
  onPress,
  primary,
  danger,
}: {
  icon: ComponentProps<typeof Feather>['name']
  label: string
  hint: string
  onPress: () => void
  primary?: boolean
  danger?: boolean
}) {
  const fb = useStyles()
  const c = useStudioChrome()
  return (
    <HintButton
      label={label}
      hint={hint}
      onPress={onPress}
      style={[
        fb.canvasBtn,
        primary && fb.canvasBtnPrimary,
        danger && fb.canvasBtnDanger,
      ]}>
      <Feather
        name={icon}
        size={primary ? 20 : 15}
        color={primary || danger ? c.onCanvas : c.onCanvasMuted}
      />
    </HintButton>
  )
}

function IconBtn({
  icon,
  color,
  onPress,
  disabled,
  label,
  hint,
}: {
  icon: ComponentProps<typeof Feather>['name']
  color: string
  onPress: () => void
  disabled?: boolean
  label: string
  hint?: string
}) {
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={hint ?? label}
      onPress={onPress}
      disabled={disabled}
      hitSlop={6}
      style={{opacity: disabled ? 0.35 : 1, padding: 2}}>
      <Feather name={icon} size={14} color={color} />
    </TouchableOpacity>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    stepScroll: {padding: 20, paddingBottom: 60, gap: 16},
    /**
     * The studio fills the centre column: page sheet, studio chrome, nothing else.
     *
     * Deliberately *unpainted*. This box is the full width of the screen —
     * `Layout.Center` narrows it to the column, not this — and the web shell
     * draws the left rail and the right rail as fixed, transparent overlays on
     * top of it. So a colour here is not a backdrop for the studio: it is a
     * backdrop for the entire window, and painting it with `stage` tinted the
     * nav and the rail the recessed desk grey while the studio itself sat white
     * in the middle of them. Leaving it alone lets the shell's own `atoms.bg`
     * show through (`view/shell/index.web.tsx`), which is what every other
     * screen wears and what the capsule studio already does.
     */
    screenWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Inside `Layout.Center`, so the studio is centred on the web shell's centre
    // column rather than on the viewport — and it fills that column's height, the
    // way the capsule studio does.
    frameColumn: {
      flex: 1,
      alignItems: 'center',
    },
    /**
     * The studio's canvas.
     *
     * Deliberately *undrawn* on its left and right edges. This box already sits
     * exactly on the shell's centre column, and `Layout.Screen` draws that
     * column's edges itself (`WebCenterBorders`, one 1px hairline a side). A
     * border here is not a second opinion, it is a second line 2px wide stacked
     * on the first at the same pixels — so the column read as 3px where the
     * capsule studio beside it reads as 1px.
     */
    studioFrame: {
      backgroundColor: c.frame,
      overflow: 'hidden',
    },
    // ── Cover step (full-bleed cover editor) ──────────────────────────────────
    coverStage: {flex: 1, backgroundColor: c.stage},
    /**
     * The empty stage: the invitation, not a picture.
     *
     * It is the studio's own surface rather than a cover canvas, because there
     * is no art yet — and the paragraphs carry a max width so the instruction
     * stays a sentence on a tablet instead of stretching into one long line.
     */
    coverEmpty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      paddingHorizontal: 28,
      backgroundColor: c.surface,
    },
    /** `[picture] or [film]`, read before the options are named in words. */
    coverEmptyIcons: {flexDirection: 'row', alignItems: 'center', gap: 10},
    coverEmptyOr: {fontSize: 12, fontWeight: '700', letterSpacing: 0.4},
    coverEmptyTitle: {
      color: c.text,
      fontSize: 20,
      fontWeight: '800',
      textAlign: 'center',
    },
    coverEmptyTxt: {
      color: c.textMuted,
      fontSize: 13,
      lineHeight: 19,
      fontWeight: '600',
      textAlign: 'center',
      maxWidth: 300,
    },
    /** The one action on this step: everything after it follows from it. */
    coverAddBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      marginTop: 4,
    },
    coverAddBtnTxt: {fontSize: 14, fontWeight: '800'},
    coverTopScrim: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 160,
    },
    coverBottomScrim: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 340,
    },
    coverTop: {position: 'absolute', top: 0, left: 0, right: 0, padding: 12},
    /** The one control over the art: a single pill, at the trailing end. */
    coverTopRow: {flexDirection: 'row', justifyContent: 'flex-end'},
    coverTab: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    /**
     * The same pill over cover art. The art is the creator's own picture, so it
     * cannot be assumed light or dark: over art the pill goes translucent black
     * with white ink, which reads on a bright sky and a night shot alike.
     */
    coverTabOnArt: {
      borderColor: c.canvasBorder,
      backgroundColor: OVER_ART.panel,
    },
    coverTabTxt: {fontSize: 12, fontWeight: '700'},
    coverBottom: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: 16,
      gap: 6,
    },
    coverTitleInput: {
      color: c.text,
      fontSize: 26,
      fontWeight: '900',
      lineHeight: 32,
      paddingVertical: 2,
      /*
       * Bounded like the description below it. This block is anchored to the
       * bottom of the stage and grows upwards, so an unbounded title would push
       * the voice-over and Continue off a short phone — a long title scrolls
       * inside its own field instead, which is also how the description behaves.
       */
      maxHeight: 96,
    },
    coverDescInput: {
      color: c.textMuted,
      fontSize: 14,
      lineHeight: 20,
      maxHeight: 64,
      paddingVertical: 2,
    },
    /**
     * Over art the title sits on the creator's own picture, so it takes the scrim
     * treatment every book cover uses: white ink with a soft shadow. Without art
     * there is nothing to fight, and the title is ordinary studio ink on the
     * studio's own surface.
     */
    coverTitleOnArt: {
      color: c.onCanvas,
      textShadowColor: OVER_ART.inkShadow,
      textShadowOffset: {width: 0, height: 1},
      textShadowRadius: 8,
    },
    coverDescOnArt: {
      color: OVER_ART.inkStrong,
      textShadowColor: OVER_ART.inkShadow,
      textShadowOffset: {width: 0, height: 1},
      textShadowRadius: 8,
    },
    // The intro-audio field stacks its player, actions and voice preview, so it
    // gets a column with a slight lift off the cover art behind it.
    coverAudioPills: {
      gap: 8,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
      borderRadius: 14,
      padding: 10,
    },
    coverAudioPillsOnArt: {
      borderColor: OVER_ART.panelBorder,
      backgroundColor: OVER_ART.panel,
    },
    coverContinue: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: c.accentFill,
      borderRadius: 16,
      paddingVertical: 14,
      marginTop: 4,
    },
    coverContinueTxt: {color: c.onAccent, fontSize: 16, fontWeight: '800'},
    // A dimmed primary button with no explanation is a dead end: this says which
    // of the two requirements is still missing.
    coverReqTxt: {
      color: c.textMuted,
      fontSize: 12.5,
      fontWeight: '600',
      textAlign: 'center',
      marginBottom: 8,
    },
    coverReqOnArt: {color: OVER_ART.inkMuted},
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      height: 52,
      backgroundColor: c.frame,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    backBtn: {padding: 4},
    headerTitle: {color: c.text, fontSize: 15, fontWeight: '800'},
    draftsBtn: {
      marginLeft: 'auto',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    draftsBtnTxt: {color: c.text, fontSize: 12, fontWeight: '700'},
    titleBtn: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      minWidth: 0,
    },
    titleTxt: {
      color: c.text,
      fontSize: 14,
      fontWeight: '700',
      flexShrink: 1,
    },
    iconPill: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: c.borderStrong,
    },
    publishBtn: {
      paddingHorizontal: 14,
      paddingVertical: 7,
      borderRadius: 20,
      backgroundColor: c.accentFill,
    },
    publishTxt: {color: c.onAccent, fontSize: 13, fontWeight: '700'},
    /**
     * The reading half of a content block.
     *
     * The bottom padding is the gap the block's audio band used to be drawn
     * without: the band's top hairline landed directly on the explanation field's
     * box, so the field looked cut off rather than finished. A band is a section of
     * the card, and a section starts after the text, not on it.
     */
    contentBody: {paddingHorizontal: 14, paddingTop: 14, paddingBottom: 14},
    audioBar: {
      flexShrink: 0,
      gap: 8,
      paddingHorizontal: 14,
      paddingTop: 12,
      paddingBottom: 12,
      borderTopWidth: 1,
      borderTopColor: c.border,
      backgroundColor: c.fill,
    },
    audioNote: {color: c.textFaint, fontSize: 11},

    // ── Rich reading blocks ────────────────────────────────────────────────────
    richField: {gap: 6, marginBottom: 12},
    richLabel: {fontSize: 10, fontWeight: '800', letterSpacing: 1.1},
    richInput: {
      color: c.text,
      fontSize: 15,
      lineHeight: 22,
      paddingHorizontal: 11,
      paddingVertical: 9,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
      minHeight: 44,
    },
    chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
    chip: {
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    chipTxt: {color: c.text, fontSize: 12.5, fontWeight: '700'},
    richPick: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      marginBottom: 12,
      padding: 9,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
    },
    richPickThumb: {width: 54, height: 42, borderRadius: 8},
    richPickEmpty: {
      width: 54,
      height: 42,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fillStrong,
    },
    richPickText: {flex: 1, minWidth: 0},
    richPickTitle: {color: c.text, fontSize: 13.5, fontWeight: '700'},
    richPickHint: {color: c.textMuted, fontSize: 11, marginTop: 2},
    gridScroll: {minWidth: '100%'},
    grid: {gap: 6},
    gridRow: {flexDirection: 'row', alignItems: 'center', gap: 6},
    gridRowNo: {width: 14, fontSize: 11, fontWeight: '800'},
    gridCell: {
      flex: 1,
      // Wide enough for a real heading; the grid scrolls rather than truncating.
      minWidth: 86,
      color: c.text,
      fontSize: 13,
      paddingHorizontal: 8,
      paddingVertical: 7,
      borderRadius: 7,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    gridCellHead: {
      fontWeight: '800',
      borderColor: c.borderStrong,
      backgroundColor: c.fillStrong,
    },
    gridDel: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.dangerTint,
    },
    gridDelOff: {opacity: 0.35},
    gridTools: {flexDirection: 'row', gap: 8, marginTop: 10},
    gridTool: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 11,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    gridToolTxt: {color: c.text, fontSize: 12, fontWeight: '700'},
    // The page itself: full column width, as tall as its blocks need, and never
    // shorter than the room it has — so a one-block page still paints a whole
    // sheet rather than a strip with the studio's background showing beneath it.
    pageScroll: {flexGrow: 1, alignItems: 'stretch'},
    /**
     * The page a creator is *editing*, which is studio furniture and therefore
     * follows Settings → Appearance like the header above it.
     *
     * It used to be painted with the book theme's shell — the colour the theme
     * puts behind its paper — which on every current theme is a deep navy. That
     * made the editor a dark slab with white cards floating on it in light mode,
     * and it wasn't even honest: the theme's own paper, type and ornaments only
     * appear in the block's in-place preview and in the reader, both of which
     * still use the theme's colours verbatim (see `blockPreviewBody`).
     *
     * `screen` rather than `stage`: the page a creator writes on continues the
     * app's own background, so the studio reads as one surface in the chosen
     * appearance. `stage` is the *recessed desk* — in light mode a step of grey
     * below white — which made the sheet look like a slab laid on the screen.
     * The cards keep their separation from `surface` and their tinted hairline,
     * so they still read as cards on the page.
     */
    pageSheet: {width: '100%', flexGrow: 1, backgroundColor: c.screen},
    pageStack: {padding: 14, gap: 12, paddingBottom: 80},
    pageTitleInput: {
      color: c.text,
      fontSize: 17,
      fontWeight: '800',
      paddingVertical: 4,
    },
    /**
     * A block on the page.
     *
     * The card sits on the app's own background, so in light mode it is white on
     * white — and its only edge, the block-type accent hairline, is around
     * 1.25:1 against the sheet. That is a line the eye reads as nothing, which
     * left the cards looking like they were floating in a single white field.
     * The lift is what makes a card read as a card: a contact shadow that draws
     * the boundary, and a wider one that gives it depth off the page.
     *
     * Web gets an explicit `boxShadow` because that is what the browser actually
     * paints; Android gets the same idea through `elevation`. iOS is left flat
     * on purpose — a shadow on an `overflow: hidden` box (which the card needs,
     * or its header band would poke out of the radius) is clipped by the
     * platform, so on iOS the accent hairline is the whole edge.
     */
    blockCard: {
      borderRadius: 14,
      borderWidth: 1,
      overflow: 'hidden',
      backgroundColor: c.surface,
      ...platform({
        web: {boxShadow: c.cardShadow},
        android: {elevation: 2},
      }),
    },
    blockChrome: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 10,
      paddingVertical: 7,
      backgroundColor: c.fill,
    },
    blockChromeLeft: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 1,
    },
    blockChromeLabel: {
      color: c.textMuted,
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    blockActions: {flexDirection: 'row', alignItems: 'center', gap: 10},
    blockPreviewClose: {
      width: 26,
      height: 26,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fillStrong,
    },
    blockPreviewBody: {paddingHorizontal: 10, paddingTop: 10, paddingBottom: 2},
    /**
     * How tall a block's editor may get before it scrolls instead of growing.
     *
     * A page is a stack of blocks, and a creator needs to be able to see the
     * block after this one — so a card stops growing at a point and scrolls its
     * remainder. The card itself keeps sizing to the region, so nothing is ever
     * *cut*: what does not fit is one scroll away.
     */
    blockEditor: {maxHeight: 460, flexShrink: 1},
    blockEditorInner: {flexGrow: 0, paddingBottom: 2},
    /**
     * The publish-blocking note sits at the top of the flagged card rather than
     * as a banner under every card: same information, none of the "every block
     * is shouting" effect, and it wraps instead of truncating at 9:16 width.
     */
    blockIssueRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: c.warningBorder,
      backgroundColor: c.warningTint,
    },
    blockIssueRowTxt: {
      flex: 1,
      color: c.warning,
      fontSize: 11,
      fontWeight: '700',
      lineHeight: 16,
    },
    blockCardFlagged: {
      backgroundColor: c.warningTint,
    },
    emptyPage: {
      alignItems: 'center',
      gap: 6,
      paddingVertical: 46,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: c.accentBorder,
      borderRadius: 16,
      backgroundColor: c.accentTint,
    },
    emptyIcon: {
      width: 44,
      height: 44,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.accentTint,
      marginBottom: 4,
    },
    emptyPageTitle: {color: c.text, fontSize: 14, fontWeight: '700'},
    emptyPageTxt: {
      color: c.textMuted,
      fontSize: 12,
      textAlign: 'center',
      paddingHorizontal: 30,
    },
    // ── Bottom page bar ────────────────────────────────────────────────────────
    pagerBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      height: 52,
      paddingHorizontal: 10,
      backgroundColor: c.frame,
      borderTopWidth: 1,
      borderTopColor: c.border,
    },
    // Floating controls over the book canvas
    canvasBtn: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.canvas,
      borderWidth: 1,
      borderColor: c.canvasBorder,
    },
    canvasBtnPrimary: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: c.accentFill,
      borderColor: c.canvasBorder,
      shadowColor: c.shadow,
      shadowOpacity: 0.45,
      shadowRadius: 10,
      shadowOffset: {width: 0, height: 4},
      elevation: 6,
    },
    canvasBtnDanger: {
      backgroundColor: c.canvasDanger,
      borderColor: c.dangerBorder,
    },
    pagerChips: {alignItems: 'center', gap: 6, paddingRight: 4},
    pageChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      maxWidth: 132,
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    pageChipActive: {
      borderColor: c.accentBorder,
      backgroundColor: c.accentTint,
    },
    pageChipNum: {color: c.textMuted, fontSize: 12, fontWeight: '800'},
    pageChipNumActive: {color: c.accent},
    pageChipTxt: {
      color: c.textMuted,
      fontSize: 12,
      fontWeight: '600',
      flexShrink: 1,
    },
    pageChipTxtActive: {color: c.text},
    addPageBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 14,
      backgroundColor: c.accentFill,
    },
    addPageTxt: {color: c.onAccent, fontSize: 12, fontWeight: '800'},
    /**
     * The sheet the theme picker opens in. It floats over the page a creator is
     * editing, so it needs its own edges: a hairline and a shadow, because on a
     * light studio a white sheet on a light page is otherwise just a change of
     * shade.
     */
    themeSheet: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '88%',
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderTopWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
      paddingTop: 10,
      zIndex: 200,
      overflow: 'hidden',
      shadowColor: c.shadow,
      shadowOpacity: 0.28,
      shadowRadius: 18,
      shadowOffset: {width: 0, height: -6},
      elevation: 12,
    },
    sheetHandle: {
      width: 38,
      height: 4,
      borderRadius: 2,
      alignSelf: 'center',
      backgroundColor: c.borderStrong,
      marginBottom: 12,
    },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingHorizontal: 16,
      marginBottom: 10,
    },
    sheetTitle: {color: c.text, fontSize: 17, fontWeight: '800'},
    sheetSub: {color: c.textMuted, fontSize: 11.5, marginTop: 1},
    sheetClose: {
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fill,
    },
    stub: {padding: 30, alignItems: 'center'},
    stubTxt: {color: c.textMuted, fontSize: 13},
  })

/** The studio's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
